import React, { useState, useEffect, useRef, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext, useParams } from 'react-router'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"
import { v4 as uuidv4 } from 'uuid'
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps';

import Reservation from './Reservation'
import OperatorReportBlock from './OperatorReportBlock'
import Rightbar from 'pages/main/cheques/Rightbar'
import CustomTooltip from "pages/parts/CustomTooltip"
import AddressSuggestField from 'pages/parts/AddressSuggestField'
import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import useDidMountEffect from 'pages/parts/useDidMountEffect'
import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { formatDate, formatMoney, getYandexSearchBounds, notify, returnSign } from 'helpers/helpers'
import {
	getDiscountsForPos,
	returnDiscount,
	sumChequeDiscounts,
	matchesDiscountOrderType,
} from 'helpers/discountEngine'
import { resolveDiscountTypeValue } from 'helpers/discountTypeValues'
import {
	applyWholeOrderAutoAdd,
	applyForDishesAutoAdd,
	filterAutoAddRulesByOrderTypeSubId,
} from 'helpers/autoAddRules'
import { resolveCallCenterOrderTypeSubId } from 'helpers/callCenterOrderType'
import { repriceModifierLines } from 'helpers/modifierPricing'
import { isConstructorDish, summarizeConstructor } from 'helpers/constructorDisplay'
import { posPlacemarkOptions, posPlacemarkProperties } from 'helpers/mapPlacemark';
import { yandexReverseGeocode } from 'helpers/yandexGeocoder'
import { hasPermission } from 'helpers/permissions'
import HintIcon from 'pages/parts/HintIcon'
import SmsCatalogPicker from 'pages/parts/SmsCatalogPicker'

const numbers = [
	{ 'id': '1', 'name': 1 }, { 'id': '2', 'name': 2 }, { 'id': '3', 'name': 3 },
	{ 'id': '4', 'name': 4 }, { 'id': '5', 'name': 5 }, { 'id': '6', 'name': 6 },
	{ 'id': '7', 'name': 7 }, { 'id': '8', 'name': 8 }, { 'id': '9', 'name': 9 },
]

// Модификатор или компонент конструктора. Компонентом бывает товар любого типа, поэтому роль
// строки задаёт не тип, а связь с блюдом: у варианта в меню — modifier_parent_id (группа),
// у строки заказа — parent_transaction_id / parent_cheque_product_id.
function isModifierLine(line) {
	if (!line) return false
	if (String(line.product_type_value ?? '').toUpperCase() === 'MODIFIER') return true
	if (Number(line.modifier_parent_id) > 0) return true
	if (line.parent_cheque_product_id != null && line.parent_cheque_product_id !== '') return true
	return line.parent_transaction_id != null && String(line.parent_transaction_id).trim() !== ''
}

// Строка живая, пока её не удалили. Статусы у колл-центра свои (-10 новый заказ,
// -11 счёт, 1 подтверждён), поэтому «новизну» строки по ним не определяем —
// заказ до пробития на кассе редактируется целиком.
function isActiveChequeLine(line) {
	return Number(line?.status) !== -1 && !line?.done
}

/** 0 / пусто = без ограничения */
function normalizeModifierLimit(value) {
	const n = Number(value)
	return Number.isFinite(n) && n > 0 ? n : 0
}

function isModifierGroupRequired(group) {
	const value = group?.required
	return value === true
		|| value === 1
		|| value === '1'
		|| value === 't'
		|| value === 'true'
		|| value === 'TRUE'
}

/** Сколько модификаторов группы нужно выбрать: required = минимум один. */
function getModifierGroupNeedQty(group) {
	const min = normalizeModifierLimit(group?.min)
	return isModifierGroupRequired(group) ? Math.max(min, 1) : min
}

function getModifierGroupId(group) {
	const id = group?.product_modifier_id ?? group?.id
	return id == null ? null : Number(id)
}

/** Потолок выбора в группе; 0 — без ограничения. */
function getModifierGroupMaxQty(group) {
	return normalizeModifierLimit(group?.max)
}

/**
 * Слот: выбрать нужно ровно столько, сколько влезает («напиток», «гарнир ×2»).
 * В нём клик по новому варианту заменяет набранное, а не упирается в максимум.
 */
function isModifierSlotGroup(group) {
	const max = getModifierGroupMaxQty(group)
	return max > 0 && (max === 1 || getModifierGroupNeedQty(group) >= max)
}

/** Есть ли в группах варианты, отмеченные в кабинете как «по умолчанию». */
function hasDefaultModifiers(groups) {
	return (groups || []).some((group) => (group.products || []).some((variant) => (
		normalizeModifierLimit(variant?.default_quantity) > 0
	)))
}

// Масштаб модалки карты: обзор вокруг заведения и максимальный — для правки найденного адреса
const MAP_ZOOM_OVERVIEW = 13
const MAP_ZOOM_ADDRESS = 19

function OrderCreate() {
	const { t } = useTranslation()
	const { cheque_id } = useParams()
	const navigate = useNavigate()
	const date = new Date()

	const mapRef = useRef(null);
	const disableRef = useRef(false)
	const refPhoneInput = useRef()
	const scrollToBottomRef = useRef(null)
	const stopList = useRef([])
	/** Телефония из сокета применяем только к событиям после входа на эту страницу (не к «висящему» payload с Orders). */
	const orderCreatePageMountedAtRef = useRef(0)
	/** Витрину по умолчанию из настроек колл-центра подставляем один раз — дальше меню меняет только оператор. */
	const defaultExternalMenuAppliedRef = useRef(false)

	const reduxAccount = useSelector(state => state.account)
	const reduxDelivery = useSelector(state => state.delivery)
	const deliveryOutlet = useOutletContext()
	const liveStopList = deliveryOutlet?.liveStopList
	const liveTelephony = deliveryOutlet?.liveTelephony

	const externalCouriers = reduxDelivery?.externalCouriers ?? [];
	const initialOrderType = 3
	const initialOrderTypeSubId = resolveCallCenterOrderTypeSubId(
		reduxAccount?.call_center,
		initialOrderType
	)

	const [data, setData] = useState({
		"payment_type_id": reduxAccount?.call_center?.payment_type_id,
		"client_phone": reduxAccount.prefix,
		"order_type": initialOrderType,
		"order_type_sub_id": initialOrderTypeSubId,
		"active_tab": 1,
		"pos_id": null,
		"total_amount": 0,
		"total_amount_service": 0,
		"delivery_price": 0,
		"discount": 0,
		"discounts": [],
		"people_number": 1,
		"service_percentage": 0,
		"eating_kit": 1,
		"products": [],
		"note": '',
		"comment": '',
		'transaction_id': uuidv4(),
	});
	const [orderType, setOrderType] = useState(initialOrderType); // 2 - Собой 3 - Доставка
	const [menu, setMenu] = useState([]);
	const [menuDuplicate, setMenuDuplicate] = useState([]);
	const [menuParent, setMenuParent] = useState({
		'parent_id': null,
		'parent_name': "",
	});
	const [products, setProducts] = useState(reduxDelivery?.products);
	const [comments, setComments] = useState([]);
	const [commentsDuplicate, setCommentsDuplicate] = useState([]);
	const [selectedMenu, setSelectedMenu] = useState({
		'products': []
	});
	// change table
	const [couriers] = useState([]);
	const [sizes, setSizes] = useState([]);
	const [modifiers, setModifiers] = useState([]);
	const [modalModifiers, setModalModifiers] = useState(false);
	const [modalModifiersForced, setModalModifiersForced] = useState(false);
	const [modalSizes, setModalSizes] = useState({
		'bool': false,
		'replace': false,
		'index': '',
	});
	const [modalModification, setModalModification] = useState({
		'bool': false,
		'index': '',
		'tabNumber': 'quantityTab',
		'product': {
			'quantity': ''
		},
	});
	const [modalMap, setModalMap] = useState(false);
	// Ключ геокодирования заведения приходит в аккаунте при логине (из /external-geocoding).
	const mapToken = reduxAccount?.map_token || '';
	const [client, setClient] = useState({
		'phone1': '',
		'phone2': '',
		'first_name': '',
		'address': '',
		'new_address': t('new_address'),
		'note': '',
		'addresses': [],
		'gps': [41.311081, 69.240562],
		'latitude': 41.311081,
		'longitude': 69.240562
	});
	const [selectedItem, setSelectedItem] = useState({});
	// ДР на момент открытия карточки: указанный гостем меняет только сотрудник с правом
	const [clientBirthdayInitial, setClientBirthdayInitial] = useState(null)
	// Предзаказ оформляет оператор с правом F_PREORDER — то же право, что у кассира
	const canPreorders = hasPermission('F_PREORDER')
	const isBirthdayLocked = !!selectedItem.id && !!clientBirthdayInitial && !hasPermission('client_birthday_edit')
	const [newAddress, setNewAddress] = useState({
		'latitude': '',
		'longitude': '',
		'new_address': '',
		'address': '',
		'pos_name': '',
		'distance': '',
	});
	const [smsTemplates, setSmsTemplates] = useState([]);
	// Mison SMS: шаблон из каталога вместо своего текста (SmsCatalogPicker)
	const [isSmsCatalog, setIsSmsCatalog] = useState(false)
	const [smsCatalog, setSmsCatalog] = useState({ template_code: null, vars: {}, locale: 'auto' })
	const [promocodes, setPromocodes] = useState([]);
	const [defaultState, setDefaultState] = useState({
		center: [41.311081, 69.240562],
		zoom: 13,
		controls: ["zoomControl"]
	});
	const [modalComment, setModalComment] = useState({ 'bool': false, 'type': 'comment' });
	// Снять позицию, которая уже ушла на кухню: право и причина — как на кассе.
	// null — ещё не загрузили, тогда решает сервер
	const [deletionSettings, setDeletionSettings] = useState(null);
	const [modalDeletionReason, setModalDeletionReason] = useState({ 'bool': false, 'action': null, 'reason_id': null, 'comment': '' });
	const [modalAddressDelete, setModalAddressDelete] = useState({ 'bool': false, 'address_id': null });
	const [modalClient, setModalClient] = useState(false);
	const [modalExternalCourier, setModalExternalCourier] = useState(false);
	const [modalExternalMenu, setModalExternalMenu] = useState(false);
	const [calculatedExternalCouriers, setCalculatedExternalCouriers] = useState([]);
	const [stopListPoses, setStopListPoses] = useState([]);
	const [stopListProducts, setStopListProducts] = useState([]);
	const [stopListSearch, setStopListSearch] = useState('');
	const [cheques, setCheques] = useState([]);
	const [modalCheques, setModalCheques] = useState(false);
	const [showRightBar, setShowRightBar] = useState(false);
	const [filterData, setFilterData] = useState({
		start_date: formatDate(date.setDate(date.getDate() - 60), 'yyyy-MM-dd'),
		end_date: formatDate(new Date(), 'yyyy-MM-dd'),
		status: 10,
		search: '',
		page: null,
	})
	const [pagination, setPagination] = useState({
		url: '/cheques',
		response: null,
	})

	const selectedExternalCouriers = calculatedExternalCouriers.length ? calculatedExternalCouriers : externalCouriers;
	const mapSearchPoses = (reduxDelivery?.poses || []).filter((pos) => {
		if (data?.pos_id) return Number(pos.id) === Number(data.pos_id)
		return true
	})
	const mapSearchBounds = getYandexSearchBounds(mapSearchPoses)

	function getExternalCourierCoordinates() {
		const selectedPos = reduxDelivery?.poses?.find(item => item.id === data?.pos_id);
		const selectedAddress = client?.addresses?.find(item => item.id === data?.address_id);
		const clientLatitude = selectedAddress?.latitude ?? newAddress?.latitude;
		const clientLongitude = selectedAddress?.longitude ?? newAddress?.longitude;

		const posLatitude = selectedPos?.latitude;
		const posLongitude = selectedPos?.longitude;

		if (
			posLatitude === undefined || posLatitude === null || posLatitude === '' ||
			posLongitude === undefined || posLongitude === null || posLongitude === '' ||
			clientLatitude === undefined || clientLatitude === null || clientLatitude === '' ||
			clientLongitude === undefined || clientLongitude === null || clientLongitude === ''
		) {
			return null;
		}

		return {
			pos_longitude: Number(posLongitude),
			pos_latitude: Number(posLatitude),
			client_longitude: Number(clientLongitude),
			client_latitude: Number(clientLatitude),
			client_id: client?.id ?? null,
			address_id: data?.address_id ?? null,
		};
	}

	async function createUpdate(params = {}) {
		if (disableRef.current) return
		disableRef.current = true

		var dataCopy = { ...data }

		if (!dataCopy.id) {
			// order_type_delivery 1-Менеджер 2-Телеграм 3-Колл центр 4-Сайт 5-Android 6-IOS
			dataCopy.order_type = orderType
			dataCopy.order_type_sub_id = resolveCallCenterOrderTypeSubId(
				reduxAccount?.call_center,
				orderType
			) ?? dataCopy.order_type_sub_id
			dataCopy.order_type_delivery = 3
			dataCopy.operator_name = reduxAccount?.first_name
			if (params?.status === 0) {
				dataCopy.status = 0
			}
			var clientCopy = { ...client }

			if (!clientCopy.id) {
				clientCopy.pos_id = reduxAccount?.pos?.id
				var clientResponse
				var clientAddressResponse

				clientResponse = await POST('/clients', clientCopy, { loader: true })
				if (httpOk(clientResponse)) {
					clientResponse = clientResponse.data
				} else {
					return;
				}
				if (newAddress?.latitude) {
					clientAddressResponse = await POST('/clients-create-address', {
						'client_id': clientResponse.data.id,
						'longitude': newAddress?.longitude,
						'latitude': newAddress?.latitude,
					}, { loader: true })
					if (httpOk(clientAddressResponse)) dataCopy.address_id = clientAddressResponse.data?.id
				}

				if (!clientResponse) {
					disableRef.current = false
					return
				}

				dataCopy.address_id = clientAddressResponse.data?.id
				dataCopy.pos_id = newAddress?.pos_id
			} else {

				dataCopy.client_id = clientResponse?.data?.id
				dataCopy.client_name = clientCopy?.client_name
				dataCopy.client_phone = clientCopy?.client_phone
				dataCopy.client_phone2 = clientCopy?.client_phone2

				dataCopy.client_id = clientCopy.id
				dataCopy.address_id = clientCopy.address_id
			}

			const response = await POST('/v1/callcenter/cheques', dataCopy, { notification: true, loader: true })
			if (httpOk(response)) {

				clearData()
				if (refPhoneInput.current) refPhoneInput.current.select()
			}

			disableRef.current = false
		}

		if (dataCopy.id) {
			const response = await PUT('/v1/callcenter/cheques', dataCopy, { notification: true, loader: true })
			if (httpOk(response)) navigate(-1)
		}

		disableRef.current = false
	}

	function clearData() {
		if (data.cheque_id) return

		const nextOrderType = 3
		const nextSubId = resolveCallCenterOrderTypeSubId(reduxAccount?.call_center, nextOrderType)
		setOrderType(nextOrderType)
		setData({
			"order_type": nextOrderType,
			"order_type_sub_id": nextSubId,
			"payment_type_id": reduxAccount?.call_center?.payment_type_id,
			"client_phone": reduxAccount.prefix,
			"active_tab": 1,
			"pos_id": null,
			"total_amount": 0,
			"total_amount_service": 0,
			"delivery_price": 0,
			"discount": 0,
			"discounts": [],
			"people_number": 1,
			"service_percentage": 0,
			"eating_kit": 1,
			"products": [],
			"note": '',
			"comment": '',
			'transaction_id': uuidv4(),
		})
		setClient({
			'phone1': '',
			'phone2': '',
			'first_name': '',
			'address': '',
			'new_address': t('new_address'),
			'note': '',
			'addresses': [],
			'gps': [41.311081, 69.240562],
			'latitude': 41.311081,
			'longitude': 69.240562
		})

		if (stopList.current.length) {
			setMenu(reduxDelivery?.menu)
			setSelectedMenu({})
		}
	}

	function getScopedAutoAddRules(orderTypeSubId) {
		return filterAutoAddRulesByOrderTypeSubId(
			reduxDelivery?.autoAddRules || [],
			orderTypeSubId
		)
	}

	async function handleChangeOrderType(type) {
		var dataCopy = { ...data }
		const nextSubId = resolveCallCenterOrderTypeSubId(reduxAccount?.call_center, type)
		const rules = getScopedAutoAddRules(nextSubId)

		setOrderType(type)

		if (type === 2) {
			dataCopy = {
				...dataCopy,
				'order_type': type,
				'order_type_sub_id': nextSubId,
				'address_id': '',
				'delivery_price': 0,
				'distance': 0,
				'apartment': null,
				'floor': null,
				'entrance': null,
				'intercom': null,
				'navigate': null,
			}
			setClient({ ...client, 'address_id': null, 'address': '' })
			calculateTotalAmount(dataCopy, { autoAddRules: rules, baseDeliveryPrice: 0 })
			return
		}

		if (type === 3) {
			dataCopy.order_type = 3
			dataCopy.order_type_sub_id = nextSubId
			const responseClosestPos = await POST('/delivery/closest-pos', {}, { loader: true })
			if (httpOk(responseClosestPos)) {
				dataCopy.delivery_amount_until_km = responseClosestPos.data.delivery_amount_until_km
				dataCopy.distance = responseClosestPos.data.distance
				dataCopy.delivery_distance = responseClosestPos.data.delivery_distance
				dataCopy.error = false
				calculateTotalAmount(dataCopy, {
					baseDeliveryPrice: responseClosestPos.data.delivery_price,
					autoAddRules: rules,
				})
			} else {
				dataCopy.error = true
				setData(dataCopy)
			}
		}
	}

	async function createClientAddress() {
		var clientCopy = { ...client }

		if (clientCopy.id) {
			const response = await POST('/clients-create-address', {
				'client_id': clientCopy.id,
				'longitude': newAddress?.longitude,
				'latitude': newAddress?.latitude,
				'full_address': newAddress?.full_address,
				'short_address': newAddress?.short_address,
			}, { loader: true })

			if (!httpOk(response)) return

			setNewAddress({
				'latitude': '',
				'longitude': '',
				'new_address': '',
				'address': '',
				'pos_name': '',
				'distance': '',
				'full_address': '',
				'short_address': '',
			})

			// Сохранённый с карты адрес сразу выбираем — как клик по его карточке: так в заказ
			// встают ближайший филиал, дистанция и доставка. Раньше адрес только добавлялся
			// в список, и торговую точку оператор выбирал вручную, хотя карта её уже нашла.
			// searchClient здесь не зовём: он сбрасывает выбранный адрес и заново
			// применяет автодобавление к корзине
			const saved = response.data
			if (saved?.id) {
				const addresses = [...(clientCopy.addresses ?? [])]
				let savedIndex = addresses.findIndex((item) => Number(item.id) === Number(saved.id))
				if (savedIndex >= 0) {
					addresses[savedIndex] = { ...addresses[savedIndex], ...saved }
				} else {
					addresses.push(saved)
					savedIndex = addresses.length - 1
				}
				clientCopy.addresses = addresses

				if (data.order_type === 3) {
					await handleSelectAddress(clientCopy, addresses[savedIndex], savedIndex)
				} else {
					setClient(clientCopy)
				}
			} else {
				searchClient()
			}
		}

		toggleModalMap()
	}

	function addToList(item = null, params = { 'instantlySetQuantity': false }, sourceData = data) {
		var dataCopy = JSON.parse(JSON.stringify(sourceData))
		let shouldForceRequiredModifiers = false

		if (item?.stop_list_quantity !== null && Number(item?.stop_list_quantity) === 0) {
			notify(t('stop_list'));
			return;
		}

		// если есть id → ищем по id, если меняем кол-во выбранной строки → по transaction_id / selectedIndex,
		// иначе по product_id среди незавершённых
		var index = -1
		if (item.id) {
			index = dataCopy.products.findIndex(x => (x.id === item.id && !x.done))
		} else if (params.instantlySetQuantity && item.transaction_id) {
			index = dataCopy.products.findIndex(x => x.transaction_id === item.transaction_id)
		} else if (
			params.instantlySetQuantity &&
			dataCopy.selectedIndex !== null &&
			dataCopy.selectedIndex !== undefined &&
			dataCopy.selectedIndex >= 0
		) {
			index = dataCopy.selectedIndex
		} else {
			index = dataCopy.products.findIndex(x => (
				x.product_id === item.product_id
				&& !x.status
				&& !x.done
				&& !x.is_auto_add
				&& !x.is_promo_gift
			))
		}

		//var index = dataCopy.products.findIndex(x => (x.product_id === item.product_id && x.status !== 0 && !x.done))
		//var index = dataCopy.products.findIndex(x => (x.product_id === item.product_id && !x.status && !x.done))

		// Правка уже стоящей строки-модификатора (кол-во, цена) идёт общим путём:
		// ниже по MODIFIER лежит только добавление модификатора к блюду, и оно
		// вешало бы на строку саму себя как родителя.
		const isExistingModifierLine = isModifierLine(item)
			&& index >= 0
			&& !!(item.id || item.transaction_id)

		if (!isModifierLine(item) || isExistingModifierLine) {
			if (index === -1 && index !== undefined) {
				item.status = 0 // 0 потому что новый и нету еще в базе если есть в базе то 1
				item.transaction_id = uuidv4()

				if (!params.instantlySetQuantity) item.quantity = 1

				if (item.stop_list_quantity > 0 && item.stop_list_quantity < item.quantity) {
					notify(t('limit'))
					return
				}

				item.total_price = item.sale_price * item.quantity
				if (!isModifierLine(item)) { // Выделить продукт если тип продукт
					for (let i = 0; i < dataCopy.products.length; i++) { // last added item selected TRUE
						dataCopy.products[i]['selected'] = false
					}
					item.selected = true
					dataCopy.selectedProduct = item
					dataCopy.selectedIndex = dataCopy.products.length
				}

				dataCopy.products.push({ ...item }); // Клонируем объект, чтобы избежать ссылки
				shouldForceRequiredModifiers = true
			} else {
				if (params.instantlySetQuantity) {
					dataCopy.products[index]['quantity'] = Number(item.quantity)
					dataCopy.products[index]['ingredients'] = item?.ingredients
				}

				if (params.instantlySetSalePrice) {
					dataCopy.products[index]['sale_price'] = Number(item.sale_price)
					dataCopy.products[index]['total_price'] =
						Number(item.sale_price) * Number(dataCopy.products[index]['quantity'])
				}

				if (params.instantlySetTotalPrice) {
					const total = Number(item.total_price) || 0
					const product = dataCopy.products[index]
					const salePrice = Number(product.sale_price) || 0

					if (product.is_divisible && salePrice > 0) {
						product.quantity = total / salePrice
						product.total_price = total
					} else {
						const quantity = Number(product.quantity) || 1
						product.sale_price = quantity > 0 ? total / quantity : total
						product.total_price = total
					}
				}

				if (
					!params.instantlySetQuantity &&
					!params.instantlySetSalePrice &&
					!params.instantlySetTotalPrice
				) {
					dataCopy.products[index]['quantity'] = Number(dataCopy.products[index]['quantity']) + 1
				}

				if (
					Number(item.stop_list_quantity) > 0 &&
					Number(item.stop_list_quantity) < Number(dataCopy.products[index]['quantity'])) {
					notify(t('limit'))
					return
				}

				dataCopy.products[index]['total_price'] =
					dataCopy.products[index]['sale_price'] *
					dataCopy.products[index]['quantity']

				for (let i = 0; i < dataCopy.products.length; i++) { // last added item selected TRUE
					dataCopy.products[i]['selected'] = false
				}
				dataCopy.products[index]['selected'] = true
				dataCopy.selectedProduct = dataCopy.products[index]
				dataCopy.selectedIndex = index
			}
		}

		if (isModifierLine(item) && !isExistingModifierLine) {
			// Найти выделенный продукт (родителя)
			const parentIndex = dataCopy.products.findIndex(p => p.selected);

			if (parentIndex === -1) {
				notify("Сначала выберите продукт");
				return;
			}

			const parent = dataCopy.products[parentIndex];
			if (!parent.transaction_id) {
				parent.transaction_id = uuidv4()
			}

			// Проверка лимита стоп-листа
			if (item.stop_list_quantity > 0 && item.stop_list_quantity < 1) {
				notify(t('limit'));
				return;
			}

			// Ищем существующий модификатор для этого же родителя
			const existingModifierIndex = dataCopy.products.findIndex(p =>
				isModifierLine(p) &&
				p.product_id === item.product_id &&
				p.parent_transaction_id === parent.transaction_id &&
				p.status === 0 &&
				!p.done
			);

			if (existingModifierIndex >= 0) {
				// Увеличить количество существующего модификатора
				if (params.instantlySetQuantity) {
					dataCopy.products[existingModifierIndex].quantity = Number(item.quantity);
				} else {
					dataCopy.products[existingModifierIndex].quantity += 1;
				}

				dataCopy.products[existingModifierIndex].total_price =
					dataCopy.products[existingModifierIndex].sale_price *
					dataCopy.products[existingModifierIndex].quantity;
			} else {
				// Создать новый модификатор
				const newModifier = {
					...item,
					quantity: 1,
					status: 0,
					selected: false,               // ❗ НЕ выделяем модификатор
					transaction_id: uuidv4(),
					parent_transaction_id: parent.transaction_id,
					parent_cheque_product_id: parent.id ?? null,
					total_price: item.sale_price
				};

				// Найти сколько модификаторов уже у родителя
				const modifiersForParent = dataCopy.products.filter(
					p => p.parent_transaction_id === parent.transaction_id && isModifierLine(p)
				);

				// Вставить модификатор под продуктом → после всех его модификаторов
				dataCopy.products.splice(parentIndex + modifiersForParent.length + 1, 0, newModifier);
			}

			// ❗ Всегда вернуть выделение на родителя
			dataCopy.products.forEach(p => p.selected = false);
			parent.selected = true;
			dataCopy.selectedProduct = parent;
			dataCopy.selectedIndex = parentIndex;

			calculateTotalAmount(dataCopy);
			setTimeout(() => {
				scrollToBottomRef.current.scrollTop = scrollToBottomRef.current.scrollHeight
			}, 100);

			return; // Завершаем выполнение — модификатор обработан полностью
		}

		if (shouldForceRequiredModifiers) {
			applyDefaultModifiers(dataCopy, dataCopy.selectedProduct)
		}

		calculateTotalAmount(dataCopy)

		if (shouldForceRequiredModifiers) {
			openModifiersAfterAdd(dataCopy.selectedProduct, dataCopy.products)
		}

		setTimeout(() => {
			scrollToBottomRef.current.scrollTop = scrollToBottomRef.current.scrollHeight
		}, 100);
	}

	// Авто добавления продуктов при создании нового заказа
	function prepareProductToAdd(item, currentData, params = { instantlySetQuantity: false }) {
		const dataCopy = JSON.parse(JSON.stringify(currentData))

		const alreadyIndex = dataCopy.products.findIndex((p) => {
			if (Number(p.product_id) !== Number(item.product_id) || p.status !== 0 || p.done) return false
			if (item.auto_add_rule_id) {
				return Number(p.auto_add_rule_id) === Number(item.auto_add_rule_id)
			}
			return !p.is_auto_add && !p.is_promo_gift
		})

		var quantity = 1
		if (params.instantlySetQuantity) quantity = item.quantity || 1

		if (alreadyIndex === -1) {
			const newItem = {
				...item,
				quantity: quantity,
				status: 0,
				transaction_id: uuidv4(),
				total_price: quantity * item.sale_price,
				selected: false,
			}
			dataCopy.products.push(newItem)
		}

		return dataCopy
	}

	function searchProducts(value) {
		if (!value) {
			setSelectedMenu({
				...selectMenu,
				search: value,
				products: [] // все продукты
			})
			return
		}

		const allProducts = menu.flatMap(c => c.products)

		const result = allProducts.filter(item =>
			(
				item?.name?.toLowerCase().includes(value.toLowerCase()) ||
				item?.barcode?.toLowerCase().includes(value.toLowerCase())
			) &&
			item.product_type_value === 'PRODUCT'
		)

		setSelectedMenu({
			...selectMenu,
			search: value,
			products: result
		})
	}

	function searchProductsBarcode(value) {
		var result

		setSelectedMenu({
			...selectMenu,
			'search': value,
			'products': result
		})
	}

	function selectSize(item) {
		var product = item.sizes.find(x => !!x.is_default_size)

		if (!product) {
			setSizes(withStopListOnSizes(item.sizes))
			setModalSizes({
				'bool': true,
				'replace': false,
				'index': '',
			})
			return
		}

		addToList(withStopListOnItem(product))
	}

	function changeProductSize() {
		var index = data.products.findIndex(x => x.selected === true)
		var product = products.find(x => x.product_id === data.products[index]['size_parent_id'])

		setSizes(withStopListOnSizes(product?.sizes))

		setModalSizes({
			'bool': true,
			'replace': true,
			'index': index,
		})
	}

	function replaceSize(item) {
		var product = data.products.find(x => x.selected === true)
		item.sizes = product.sizes
		deleteSelectedProduct()
		addToList(item)
		setModalSizes({
			'bool': false,
			'replace': false,
			'index': '',
		})
	}

	function clickProductQuantity(value) {
		var modalModificationCopy = { ...modalModification }
		if (!modalModificationCopy.product.quantity) {
			modalModificationCopy.product.quantity = ''
		}

		if (value === 'C') {
			modalModificationCopy.product.quantity = ''
			setModalModification(modalModificationCopy)
			return
		}

		modalModificationCopy.product.quantity = modalModificationCopy.product.quantity + value
		setModalModification(modalModificationCopy)
	}

	function clickProductSalePrice(value) {
		var modalModificationCopy = { ...modalModification }
		if (!modalModificationCopy.product.sale_price) {
			modalModificationCopy.product.sale_price = ''
		}

		if (value === 'C') {
			modalModificationCopy.product.sale_price = ''
			setModalModification(modalModificationCopy)
			return
		}

		modalModificationCopy.product.sale_price = String(modalModificationCopy.product.sale_price) + value
		setModalModification(modalModificationCopy)
	}

	function clickProductTotalPrice(value) {
		var modalModificationCopy = { ...modalModification }
		if (!modalModificationCopy.product.total_price) {
			modalModificationCopy.product.total_price = ''
		}

		if (value === 'C') {
			modalModificationCopy.product.total_price = ''
			setModalModification(modalModificationCopy)
			return
		}

		modalModificationCopy.product.total_price = String(modalModificationCopy.product.total_price) + value
		applyTotalPriceModification(modalModificationCopy)
		setModalModification(modalModificationCopy)
	}

	function applyTotalPriceModification(modalModificationCopy) {
		const total = Number(modalModificationCopy.product.total_price) || 0
		const product = modalModificationCopy.product
		const salePrice = Number(product.sale_price) || 0

		if (product.is_divisible && salePrice > 0) {
			modalModificationCopy.product.quantity = Number((total / salePrice).toFixed(3))
			return
		}

		const quantity = Number(product.original_quantity || product.quantity) || 1
		if (quantity > 0 && total > 0) {
			modalModificationCopy.product.sale_price = total / quantity
		}
	}

	function handleSelectModificationTab(tabNumber) {
		var modalModificationCopy = JSON.parse(JSON.stringify(modalModification))

		if (tabNumber === 'quantityTab') {
			modalModificationCopy.tabNumber = tabNumber
			modalModificationCopy.product.quantity = ''
			setModalModification(modalModificationCopy)
			return
		}

		if (tabNumber === 'totalPriceTab') {
			modalModificationCopy.tabNumber = tabNumber
			modalModificationCopy.product.quantity = modalModificationCopy.product.original_quantity
			modalModificationCopy.product.total_price = ''
			setModalModification(modalModificationCopy)
			return
		}

		if (tabNumber === 'salePriceTab') {
			modalModificationCopy.tabNumber = tabNumber
			modalModificationCopy.product.quantity = modalModificationCopy.product.original_quantity
			modalModificationCopy.product.sale_price = ''
			setModalModification(modalModificationCopy)
			return
		}

		modalModificationCopy.tabNumber = tabNumber
		modalModificationCopy.product.quantity = modalModificationCopy.product.original_quantity
		setModalModification(modalModificationCopy)
	}

	function handleChangeModificationQuantity(e) {
		var modalModificationCopy = { ...modalModification }
		modalModificationCopy.product.quantity = e.target.value.replace(/[^0-9.]/gi, '')
		setModalModification(modalModificationCopy)
	}

	function handleChangeModificationSalePrice(e) {
		var modalModificationCopy = { ...modalModification }
		modalModificationCopy.product.sale_price = e.target.value.replace(/[^0-9.]/gi, '')
		setModalModification(modalModificationCopy)
	}

	function handleChangeModificationTotalPrice(e) {
		var modalModificationCopy = { ...modalModification }
		modalModificationCopy.product.total_price = e.target.value.replace(/[^0-9.]/gi, '')
		applyTotalPriceModification(modalModificationCopy)
		setModalModification(modalModificationCopy)
	}

	function incrementDouble() {
		var modalModificationCopy = { ...modalModification }
		if (!modalModificationCopy.product.initial_quantity) {
			modalModificationCopy.product.initial_quantity = modalModificationCopy.product.quantity
		}

		modalModificationCopy.product.quantity =
			(
				Number(modalModificationCopy.product.quantity) +
				Number(modalModificationCopy.product.initial_quantity)
			).toFixed(1)
		setModalModification(modalModificationCopy)
	}

	async function getDeletionSettings() {
		const response = await GET('/v1/callcenter/deletion-settings', {}, { loader: false })
		if (httpOk(response)) setDeletionSettings(response.data)
	}

	// Позиция уже ушла на кухню: сохранена и подтверждена (status 1). Неподтверждённую
	// (status 0) повар ещё не видел, её правят свободно
	function isSentToKitchenLine(line) {
		return !!line?.id && Number(line.status) > 0
	}

	// Пропускает снятие позиции или спрашивает причину; action — что сделать после выбора
	function canRemoveSentLine(line, action) {
		if (!isSentToKitchenLine(line) || !deletionSettings) return true

		if (!deletionSettings.can_delete_sent) {
			notify(t('delete_sent_no_access'))
			return false
		}

		if (deletionSettings.is_reason_required && !line.deletion_reason_id) {
			setModalDeletionReason({ 'bool': true, 'action': action, 'reason_id': null, 'comment': '' })
			return false
		}

		return true
	}

	function applyDeletionReason() {
		const reason = (deletionSettings?.reasons || []).find(x => x.id === modalDeletionReason.reason_id)
		if (!reason) return

		const comment = (modalDeletionReason.comment || '').trim()
		if (reason.is_comment_required && !comment) {
			notify(t('deletion_reason_comment_required'))
			return
		}

		const dataCopy = JSON.parse(JSON.stringify(data))
		const index = dataCopy.products.findIndex(x => x.selected === true && x.status !== -1)
		setModalDeletionReason({ 'bool': false, 'action': null, 'reason_id': null, 'comment': '' })
		if (index === -1) return

		dataCopy.products[index]['deletion_reason_id'] = reason.id
		dataCopy.products[index]['deletion_reason_comment'] = comment || null

		if (modalDeletionReason.action === 'delete') {
			deleteSelectedProduct(dataCopy)
		} else {
			instantChangeSelectedProductQuantity('-', dataCopy)
		}
	}

	function instantChangeSelectedProductQuantity(type, sourceData = data) {
		var index = sourceData.products.findIndex(x => x.selected === true)
		if (index === -1) return

		var product = sourceData.products[index]
		if (!product) return
		if (product.id && type === '+') return
		if (isModifierLine(product)) return
		if (product.is_promo_gift || product.is_required || product.is_auto_add) return

		if (type === '+') {
			addToList({
				...product,
				quantity: Number(product.quantity) + 1,
			}, { 'instantlySetQuantity': true })
			return
		}

		if (type === '-') {
			const nextQuantity = Number(product.quantity) - 1
			if (nextQuantity <= 0) {
				deleteSelectedProduct(sourceData)
				return
			}
			if (!canRemoveSentLine(product, 'decrease')) return
			addToList({
				...product,
				quantity: nextQuantity,
			}, { 'instantlySetQuantity': true }, sourceData)
		}
	}

	function changeSelectedProductQuantity() {
		var product = data.products.find(x => x.selected === true)
		var index = data.products.findIndex(x => x.selected === true)
		if (index === -1 || !product) return
		if (product.is_promo_gift || product.is_required || product.is_auto_add) return

		if (!product.cheque_id) {
			const productClone = {
				...product,
				quantity: String(product.quantity ?? ''),
				original_quantity: product.quantity,
				total_price: '',
				original_total_price: product.total_price,
				sale_price: String(product.sale_price ?? ''),
				original_sale_price: product.sale_price,
				comment: product.comment ?? '',
			}

			setModalModification({
				...modalModification,
				'bool': true,
				'tabNumber': 'quantityTab',
				'product': productClone,
				'index': index
			})
		}
	}

	function deleteSelectedProduct(sourceData = data) {
		var dataCopy = { ...sourceData }
		var index = dataCopy.products.findIndex(x => (x.selected === true && x.status !== -1))
		if (index >= 0) {
			const selected = dataCopy.products[index]
			if (selected?.is_promo_gift || selected?.is_required || selected?.is_auto_add) return
			if (breaksRequiredModifierGroup(selected, null, dataCopy.products)) {
				notifyRequiredModifierLocked()
				return
			}
			if (!canRemoveSentLine(selected, 'delete')) return
		}

		if (
			index >= 0 &&
			dataCopy.products[index]['id'] &&
			dataCopy.products[index]['status'] !== -1) {

			const parentLine = dataCopy.products[index]

			dataCopy.selectedProduct = undefined
			dataCopy.selectedIndex = undefined
			dataCopy.products[index]['status'] = -1
			dataCopy.products[index]['quantity'] = 0
			dataCopy.products[index]['selected'] = false

			// Модификаторы блюда уходят вместе с ним — иначе в заказе останется
			// «Сыр x1» без самого блюда. Сохранённые помечаем удалёнными (бэкенд
			// снимет их по status -1), не сохранённые просто убираем из списка.
			if (!isModifierLine(parentLine)) {
				dataCopy.products = dataCopy.products.filter((line) => {
					if (!isModifierLine(line)) return true
					if (Number(line.status) === -1) return true
					if (!isModifierLineOfParent(line, parentLine)) return true

					if (!line.id) return false

					line.status = -1
					line.quantity = 0
					line.selected = false
					// Причина у модификатора та же, что у блюда
					line.deletion_reason_id = parentLine.deletion_reason_id ?? null
					line.deletion_reason_comment = parentLine.deletion_reason_comment ?? null
					return true
				})
			}

			calculateTotalAmount(dataCopy)
			return
		}

		if (index !== -1 && index !== undefined) {
			// Модификаторы уходят вместе с блюдом: иначе в заказе остаётся
			// «Сыр x1» без самого блюда
			const parentLine = dataCopy.products[index]
			if (!isModifierLine(parentLine)) {
				dataCopy.products = dataCopy.products.filter((line) => (
					line !== parentLine
					&& (!isModifierLine(line) || !isModifierLineOfParent(line, parentLine))
				))
			} else {
				dataCopy.products.splice(index, 1)
			}

			dataCopy.selectedProduct = undefined
			dataCopy.selectedIndex = undefined
			setData(dataCopy)
			calculateTotalAmount(dataCopy)
		}
	}

	function selectProduct(index) {
		var dataCopy = { ...data }

		if (dataCopy.products[index]['status'] === -1) return

		for (let i = 0; i < dataCopy.products.length; i++) { // last added item selected TRUE
			dataCopy.products[i]['selected'] = false
		}
		dataCopy.products[index]['selected'] = true
		dataCopy.selectedProduct = dataCopy.products[index]
		dataCopy.selectedIndex = index
		setData(dataCopy)
	}

	function acceptModificationChange() {
		const isQuantityTab = modalModification.tabNumber === 'quantityTab'
		if (isQuantityTab && isModifierLine(modalModification.product)) {
			const removed = Number(modalModification.product.original_quantity || 0)
				- Number(modalModification.product.quantity || 0)
			if (removed > 0 && breaksRequiredModifierGroup(modalModification.product, removed)) {
				notifyRequiredModifierLocked()
				return
			}
		}

		addToList(modalModification.product, {
			'instantlySetQuantity': modalModification.tabNumber === 'quantityTab'
				|| modalModification.tabNumber === 'ingredientTab',
			'instantlySetSalePrice': modalModification.tabNumber === 'salePriceTab',
			'instantlySetTotalPrice': modalModification.tabNumber === 'totalPriceTab',
		})

		setModalModification({
			'tabNumber': 'quantityTab',
			'bool': false,
			'product': {},
			'index': ''
		})
	}

	function selectMenu(index) {
		var menuCopy = [...menu]

		for (let i = 0; i < menuCopy.length; i++) {
			if (i === index) {
				menuCopy[i]['active'] = true
				if (menuCopy[i]?.children.length === 0) setSelectedMenu(menuCopy[i])
			} else {
				menuCopy[i]['active'] = false
			}
		}

		if (menuCopy[index]?.children.length === 0) setMenu(menuCopy)

		if (menuCopy[index]?.children.length !== 0) {
			setMenu(menuCopy[index]['children'])
			setMenuParent({
				'parent_id': menuCopy[index]['id'],
				'color': menuCopy[index]['color'] ?? null,
				'bg_color': menuCopy[index]['bg_color'] ?? null,
			})
		}
	}

	function handleBackClick() {
		// Мы уже на самом верхнем уровне (Главное меню), нет смысла идти назад
		if (menuParent.parent_id === null) return;

		// Находим родительский узел текущего уровня
		const parentNode = findNodeById(menuDuplicate, menuParent.parent_id);

		if (parentNode && parentNode.parent_id !== undefined) { // Проверяем, что родительский узел найден и у него есть parent_id (то есть это не корень)
			const grandparentNode = findNodeById(menuDuplicate, parentNode.parent_id);

			if (grandparentNode) {
				setMenu(grandparentNode.children); // Показываем дочерние элементы дедушки
				setMenuParent({
					'parent_id': grandparentNode.id,
					'color': grandparentNode.color ?? null,
					'bg_color': grandparentNode.bg_color ?? null,
				}); // Дедушка становится новым родителем
			} else {
				// Если дедушка не найден (значит, текущий родитель - это корневой элемент, например "Main")
				setMenu(menuDuplicate.filter(node => node.parent_id === null)); // Показываем все корневые элементы
				setMenuParent({}); // Переходим на уровень без родителя
			}
			setSelectedMenu(null); // Сбрасываем выделение при возврате
		}
	}

	function findNodeById(nodes, id) {
		for (const node of nodes) {
			if (node.id === id) {
				return node;
			}

			if (node.children && node.children.length > 0) {
				const found = findNodeById(node.children, id);
				if (found) {
					return found;
				}
			}
		}

		return null;
	}

	function goTo(page) {
		if (page === "back") {
			navigate('/delivery/orders')
			return
		}
	}

	function resolveDeliveryPrice(dataCopy, params = {}) {
		const { baseDeliveryPrice = null } = params;

		if (dataCopy.order_type !== 3) {
			dataCopy.delivery_price = 0;
			return;
		}

		if (dataCopy.use_external_delivery_price) {
			const lockedExternalPrice = dataCopy.external_delivery_price;
			if (lockedExternalPrice !== null && lockedExternalPrice !== undefined && lockedExternalPrice !== '' && !Number.isNaN(Number(lockedExternalPrice))) {
				dataCopy.delivery_price = Number(lockedExternalPrice);
				return;
			}
		}

		const selectedExternalCourier = externalCouriers.find(item => item.id === dataCopy.external_courier_id);
		if (selectedExternalCourier?.is_delivery_price_override) {
			const overridePrice = dataCopy.external_delivery_price ?? selectedExternalCourier?.delivery_price;
			if (overridePrice !== null && !Number.isNaN(Number(overridePrice))) {
				dataCopy.delivery_price = Number(overridePrice);
				return;
			}
		}

		if (baseDeliveryPrice !== null && baseDeliveryPrice !== undefined && baseDeliveryPrice !== '') {
			dataCopy.delivery_price = Number(baseDeliveryPrice);
			return;
		}

		if (reduxAccount?.call_center?.delivery_rule_value === 'ALTERNATIVE_BY_AMOUNT') {
			if (reduxAccount?.call_center?.delivery_rule_options?.length > 0) {
				const amountForRule = Number(dataCopy.total_amount) || 0
				const matchedRule = reduxAccount?.call_center?.delivery_rule_options.find(rule =>
					Number(rule.from_price) <= amountForRule && Number(rule.to_price) >= amountForRule
				);
				// Нашли правило — обновляем. Нет правила — не затираем уже посчитанную доставку (closest-pos и т.п.)
				if (matchedRule) {
					dataCopy.delivery_price = Number(matchedRule.delivery_price)
				}
			}
		}
	}

	function calculateTotalAmount(dataCopy, params = {}) {
		if (!Array.isArray(dataCopy.discounts)) dataCopy.discounts = []

		// Конструктор: бесплатный лимит группы и «входит в цену» раскладывают вариант на платную и
		// бесплатные строки. Пересобираем до расчёта суммы — после каждого изменения состава.
		const repriced = repriceModifierLines(dataCopy.products, {
			isEditable: isActiveChequeLine,
			createTransactionId: uuidv4,
		})
		if (repriced.changed) {
			dataCopy.products = repriced.products
			const selectedIndex = dataCopy.products.findIndex((product) => product.selected)
			if (selectedIndex >= 0) {
				dataCopy.selectedIndex = selectedIndex
				dataCopy.selectedProduct = dataCopy.products[selectedIndex]
			}
		}
		const previousDeliveryPrice = Number(dataCopy.delivery_price) || 0
		dataCopy['discount'] = 0
		dataCopy['total_amount'] = 0
		dataCopy['total_amount_service'] = 0

		const catalogProducts = reduxDelivery?.products || products || []
		const rulesForAutoAdd = params.autoAddRules
			?? getScopedAutoAddRules(dataCopy.order_type_sub_id)
		if (Array.isArray(rulesForAutoAdd) && rulesForAutoAdd.length > 0) {
			dataCopy = applyForDishesAutoAdd(
				dataCopy,
				rulesForAutoAdd,
				catalogProducts,
				prepareProductToAdd
			)
		}

		// Gift lines rebuilt by returnDiscount
		dataCopy.products = (dataCopy.products || []).filter((product) => !product.is_promo_gift)

		for (let i = 0; i < dataCopy.products.length; i++) {
			if (dataCopy.products[i]['status'] >= 0 || dataCopy.products[i]['status'] === -10) {
				dataCopy.products[i]['total_price'] = Number(dataCopy.products[i]['sale_price']) * Number(dataCopy.products[i]['quantity'])
				dataCopy['total_amount'] += Number(dataCopy.products[i]['total_price'])
			}
		}

		dataCopy['total_amount_service'] = Number(dataCopy['total_amount'])

		const rules = getDiscountsForPos(reduxDelivery?.discounts, dataCopy.pos_id)
		if (dataCopy.pos_id) {
			returnDiscount(dataCopy, rules, {
				orderTypeId: dataCopy.order_type,
				orderTypeSubId: dataCopy.order_type_sub_id,
				catalogProducts,
				clientCountCheques: client?.id ? client?.count_cheques : null,
			})
		}

		// Сохраняем доставку до resolve — иначе пересчёт скидки может обнулить closest-pos
		if (dataCopy.delivery_price == null || dataCopy.delivery_price === '') {
			dataCopy.delivery_price = previousDeliveryPrice
		}

		resolveDeliveryPrice(dataCopy, params)

		if (
			(dataCopy.delivery_price == null || dataCopy.delivery_price === '' || Number.isNaN(Number(dataCopy.delivery_price)))
			&& previousDeliveryPrice
			&& dataCopy.order_type === 3
		) {
			dataCopy.delivery_price = previousDeliveryPrice
		}

		if (dataCopy['delivery_price']) {
			dataCopy['total_amount_service'] += Number(dataCopy['delivery_price'])
		}

		dataCopy['discount'] = sumChequeDiscounts(dataCopy)
		dataCopy['total_amount_service'] -= Number(dataCopy['discount'] || 0)

		setData(dataCopy)
	}

	function toggleFixedDiscount(item) {
		const dataCopy = JSON.parse(JSON.stringify(data))
		if (!Array.isArray(dataCopy.discounts)) dataCopy.discounts = []

		const existingIndex = dataCopy.discounts.findIndex(
			(d) => d.entity_type === 'discount_fixed' && Number(d.entity_id) === Number(item.id)
		)

		if (existingIndex !== -1) {
			dataCopy.discounts.splice(existingIndex, 1)
		} else {
			dataCopy.discounts.push({
				status: 0,
				cheque_amount: 0,
				discount_type: item.discount_type_id ?? item.discount_type,
				discount_type_id: item.discount_type_id ?? item.discount_type,
				discount_type_value: resolveDiscountTypeValue(item),
				discount_service_id: item.discount_service_id,
				discount_payment_type: 2,
				discount_payment_value: item.discount_payment_value,
				discount: Number(dataCopy.total_amount || 0) * Number(item.discount_payment_value) / 100,
				total_discount: Number(dataCopy.total_amount || 0) * Number(item.discount_payment_value) / 100,
				entity_id: item.id,
				entity_type: 'discount_fixed',
			})
		}

		calculateTotalAmount(dataCopy)
	}

	async function searchClient() {
		var dataCopy = { ...data }

		if (data.client_phone.length === 12) {
			var response = await GET(`/search-client/${data.client_phone}`, {}, { loader: true })
			if (httpOk(response)) {
				response = response.data
				if (response.id) {
					await getStopList()

					dataCopy.client_id = response.id

					setClient({
						...client,
						'id': response.id,
						'phone1': data.client_phone,
						'phone2': response.phone2,
						'first_name': response.first_name,
						'client_phone': data.client_phone,
						'client_phone2': response.phone2,
						'client_name': response.client_name,
						'note': response.note,
						'gender': response.gender,
						'birthday': response.birthday,
						'address_id': null,
						'addresses': response?.addresses,
						'loyalty_balance': response.loyalty_balance,
						'telegram_user_id': response.telegram_user_id,
						'count_cheques': response.count_cheques,
						'exist': true,
					})

					const scopedRules = getScopedAutoAddRules(dataCopy.order_type_sub_id)
					if (scopedRules.length > 0) {
						dataCopy = applyWholeOrderAutoAdd(
							dataCopy,
							scopedRules,
							reduxDelivery.products || [],
							prepareProductToAdd
						)
					}

					if (reduxAccount?.call_center?.delivery_price && orderType === 3) {
						dataCopy.delivery_price = reduxAccount?.call_center?.delivery_price
					}

					if (reduxDelivery?.poses?.length === 1) {
						dataCopy.pos_id = reduxAccount?.call_center?.delivery_price

						dataCopy.pos_id = reduxDelivery.poses[0]['id'];
						dataCopy.distance = 0;

						applyStopListForPos(reduxDelivery.poses[0]['id'])

						dataCopy.apartment = ""
						dataCopy.floor = ""
						dataCopy.entrance = ""
						dataCopy.intercom = ""
						dataCopy.navigate = ""
					}

					calculateTotalAmount(dataCopy)
				} else {
					setClient({
						...client,
						'id': '',
						'client_id': '',
						'address_id': '',
						'phone2': '',
						'first_name': '',
						'address': '',
						'note': '',
						'addresses': [],
						'exist': false,
					})
				}
			}
		}
	}

	function deleteIngredient(index) {
		var modalModificationCopy = JSON.parse(JSON.stringify(modalModification))
		if (!modalModificationCopy.product.ingredients[index]['changeable']) return
		modalModificationCopy.product.ingredients[index]['del'] =
			!modalModificationCopy.product.ingredients[index]['del']

		setModalModification(modalModificationCopy)
	}

	function toggleModalMap(bool = false) {
		setModalMap(bool)
		if (!bool) {
			setNewAddress({
				...newAddress,
				'latitude': '',
				'longitude': '',
				'new_address': '',
				'address': '',
				'pos_name': '',
				'distance': '',
			})
		}
	}

	async function handleSelectAddress(clientCopy = {}, item, index) {
		var dataCopy = { ...data }
		var responseClosestPos = await POST('/delivery/closest-pos', {
			"address_id": item.id,
			"total_amount_service": data?.total_amount_service ?? 0,
		}, { loader: true })

		if (httpOk(responseClosestPos)) {
			responseClosestPos = responseClosestPos.data
			clientCopy.addresses[index]['pos_name'] = responseClosestPos.name
			clientCopy.addresses[index]['distance'] = responseClosestPos.distance

			clientCopy.address_id = item.id
			clientCopy.address = item.name

			dataCopy.apartment = item.apartment ?? null
			dataCopy.floor = item.floor ?? null
			dataCopy.entrance = item.entrance ?? null
			dataCopy.intercom = item.intercom ?? null
			dataCopy.navigate = item.navigate ?? null

			dataCopy.address_id = item.id
			dataCopy.pos_id = responseClosestPos.id
			dataCopy.distance = responseClosestPos.distance
			dataCopy.delivery_price = responseClosestPos.delivery_price
			dataCopy.delivery_distance = responseClosestPos.delivery_distance
			dataCopy.delivery_zone_id = responseClosestPos.delivery_zone_id || null
			dataCopy.delivery_zone_name = responseClosestPos.delivery_zone_name || null
			dataCopy.delivery_minutes = responseClosestPos.delivery_minutes || null

			applyStopListForPos(responseClosestPos?.id)

			setClient(clientCopy)
			calculateTotalAmount(dataCopy, { baseDeliveryPrice: responseClosestPos.delivery_price })
		} else {
			responseClosestPos = responseClosestPos.data
			clientCopy.addresses[index]['pos_name'] = responseClosestPos.pos.name
			clientCopy.addresses[index]['distance'] = responseClosestPos.pos.distance

			setClient(clientCopy)
		}
	}

	function revalidateCartForPos(dataCopy, posId) {
		const removedNames = []
		const adjustedNames = []

		if (!posId || !Array.isArray(stopList.current) || !stopList.current.length || !dataCopy.products?.length) {
			return { removedNames, adjustedNames }
		}

		const findStop = (productId) =>
			stopList.current.find(s =>
				Number(s.product_id) === Number(productId) && Number(s.pos_id) === Number(posId)
			)

		const removedParentTxIds = new Set()
		const keptProducts = []

		for (const product of dataCopy.products) {
			if (product.status === -1) {
				keptProducts.push(product)
				continue
			}

			const stop = findStop(product.product_id)

			if (stop && Number(stop.quantity) === 0) {
				removedNames.push(product.name)
				if (!isModifierLine(product) && product.transaction_id) {
					removedParentTxIds.add(product.transaction_id)
				}
				continue
			}

			if (stop && Number(stop.quantity) > 0 && Number(stop.quantity) < Number(product.quantity)) {
				product.quantity = Number(stop.quantity)
				product.total_price = Number(product.sale_price) * Number(product.quantity)
				adjustedNames.push(product.name)
			}

			keptProducts.push(product)
		}

		// Удаляем модификаторы, чей родитель ушёл в стоп на новой точке.
		dataCopy.products = keptProducts.filter(p => !(
			isModifierLine(p) &&
			p.parent_transaction_id &&
			removedParentTxIds.has(p.parent_transaction_id)
		))

		return { removedNames, adjustedNames }
	}

	async function handleSelectPos(option) {
		var dataCopy = { ...data }
		const nextPosId = option ? option.id : null
		const posChanged = String(dataCopy.pos_id ?? '') !== String(nextPosId ?? '')

		if (posChanged) {
			const { removedNames, adjustedNames } = revalidateCartForPos(dataCopy, nextPosId)
			if (removedNames.length) notify(`${t('stop_list')}: ${removedNames.join(', ')}`)
			if (adjustedNames.length) notify(`${t('limit')}: ${adjustedNames.join(', ')}`)
		}

		dataCopy.pos_id = nextPosId;
		if (!option?.id) {
			dataCopy.distance = 0;
			calculateTotalAmount(dataCopy)
			return;
		}

		if (dataCopy.order_type === 3 && dataCopy.address_id) {
			const responseClosestPos = await POST('/delivery/closest-pos', {
				"address_id": dataCopy.address_id,
				"pos_id": option.id,
				"total_amount_service": dataCopy.total_amount_service ?? 0,
			}, { loader: true })

			if (httpOk(responseClosestPos)) {
				const responseData = responseClosestPos.data
				dataCopy.distance = responseData.distance ?? 0
				dataCopy.delivery_distance = responseData.delivery_distance ?? dataCopy.delivery_distance

				const clientCopy = { ...client }
				const addressIndex = clientCopy?.addresses?.findIndex(x => x.id === dataCopy.address_id)
				if (addressIndex >= 0) {
					clientCopy.addresses[addressIndex]['pos_name'] = responseData.name ?? option.name
					clientCopy.addresses[addressIndex]['distance'] = responseData.distance ?? 0
					setClient(clientCopy)
				}

				calculateTotalAmount(dataCopy, { baseDeliveryPrice: responseData.delivery_price })
			} else {
				dataCopy.distance = 0
				calculateTotalAmount(dataCopy)
			}
		} else {
			dataCopy.distance = 0
			calculateTotalAmount(dataCopy)
		}

		applyStopListForPos(option?.id)
	}

	function applyStopListForPos(posId) {
		if (!posId || !menuDuplicate.length) return

		const updatedTree = applyStopListToMenu(posId)
		setMenuDuplicate(updatedTree)

		// Сохраняем текущий уровень навигации, но пересобираем его из нового дерева,
		// чтобы бейджи стоп-листа обновились под выбранную точку.
		if (menuParent?.parent_id) {
			const currentParent = findNodeById(updatedTree, menuParent.parent_id)
			setMenu(currentParent?.children ?? updatedTree.filter(x => x.parent_id === null))
		} else {
			setMenu(updatedTree.filter(x => x.parent_id === null))
		}

		// Пересинхронизируем открытую категорию (сетка товаров рендерится из неё).
		if (selectedMenu?.id) {
			const updatedSelected = findNodeById(updatedTree, selectedMenu.id)
			if (updatedSelected) setSelectedMenu(updatedSelected)
		}
	}

	function findStopListItem(productId, posId = data.pos_id) {
		if (productId == null || !posId) return null
		const list = Array.isArray(stopList.current) ? stopList.current : []
		return list.find((s) =>
			Number(s.product_id) === Number(productId) && Number(s.pos_id) === Number(posId)
		) || null
	}

	function withStopListOnItem(item, posId = data.pos_id) {
		if (!item) return item
		const stop = findStopListItem(item.product_id, posId)
		return {
			...item,
			stop_list_quantity: stop ? stop.quantity : null,
		}
	}

	function withStopListOnSizes(sizes, posId = data.pos_id) {
		return (sizes || []).map((size) => withStopListOnItem(size, posId))
	}

	function withStopListOnModifiers(modifiers, posId = data.pos_id) {
		return (modifiers || []).map((modifier) => ({
			...withStopListOnItem(modifier, posId),
			products: (modifier.products || []).map((child) => withStopListOnItem(child, posId)),
		}))
	}

	function applyStopListToMenu(pos_id) {
		function traverse(menu) {
			return menu.map(node => ({
				...node,
				products: node.products?.map(product => ({
					...withStopListOnItem(product, pos_id),
					sizes: withStopListOnSizes(product.sizes, pos_id),
					modifiers: withStopListOnModifiers(product.modifiers, pos_id),
				})),
				children: node.children ? traverse(node.children) : []
			}))
		}

		return traverse(menuDuplicate)
	}

	function clearSelectedAddress(dataCopy) {
		dataCopy.address_id = null
		dataCopy.distance = 0
		dataCopy.apartment = null
		dataCopy.floor = null
		dataCopy.entrance = null
		dataCopy.intercom = null
		dataCopy.navigate = null
		setClient({ ...client, 'address_id': null, 'address': '' })
		calculateTotalAmount(dataCopy, { baseDeliveryPrice: dataCopy?.delivery_amount_until_km })
	}

	function addCommentProduct(item) {
		if (item?.children?.length) {
			setComments(item.children)
			return
		}

		var dataCopy = { ...data }
		dataCopy.products[modalModification.index]['comment'] = item.name
		setData(dataCopy)
		setComments(commentsDuplicate)
		setModalModification({
			'bool': false,
			'index': '',
			'tabNumber': 'quantityTab',
			'product': {
				'quantity': ''
			},
		})
	}

	function addCommentProductAccept() {
		var dataCopy = { ...data }
		dataCopy.products[modalModification.index]['comment'] = modalModification.product.comment
		setData(dataCopy)
		setComments(commentsDuplicate)
		setModalModification({
			'bool': false,
			'index': '',
			'tabNumber': 'quantityTab',
			'product': {
				'quantity': ''
			},
		})
	}

	function addComment(item) {
		if (item?.children?.length) {
			setComments(item.children)
			return
		}

		var dataCopy = { ...data }
		if (modalComment.type === 'comment')
			dataCopy.comment = item.name
		if (modalComment.type === 'note')
			dataCopy.note = item.name

		setData(dataCopy)
		toggleModalComment(false)
	}

	async function findClosestPos(obj = { 'latitude': '', 'longitude': '', 'full_address': '', 'short_address': '' }) {
		const address = obj.address || obj.full_address || ''

		const response = await POST('/delivery/closest-pos', {
			"latitude": obj.latitude,
			"longitude": obj.longitude,
			"address": address,
		}, { loader: true })

		if (httpOk(response)) {
			setNewAddress((prev) => ({
				...prev,
				'latitude': obj.latitude,
				'longitude': obj.longitude,
				'new_address': address,
				'address': address,
				'full_address': obj.full_address ?? "",
				'short_address': obj.short_address ?? "",
				'pos_id': response.data.id,
				'pos_name': response.data.name,
				'distance': response.data.distance,
			}))
		}
	}

	// Выбор адреса из подсказок: считаем ближайший pos, центрируем карту на точке
	// и открываем модалку для корректировки метки (если правок нет — сразу «Сохранить»).
	async function handleAddressSuggestPick(picked) {
		if (!client.id) {
			notify(t('please_create_client'))
			return
		}
		await findClosestPos(picked)
		openMapAt([picked.latitude, picked.longitude], MAP_ZOOM_ADDRESS)
	}

	// Модалка карты открывается в своём масштабе: по найденному адресу — вплотную, чтобы поправить
	// метку; с иконки карты — обзор вокруг заведения. defaultState читается при каждом открытии.
	function openMapAt(center, zoom) {
		setDefaultState({ center: center, zoom: zoom, controls: ["zoomControl"] })
		toggleModalMap(true)
	}

	function openMapOverview() {
		const poses = reduxDelivery?.poses ?? []
		const center = (poses.length > 0 && poses[0]['longitude'])
			? [poses[0]['latitude'], poses[0]['longitude']]
			: defaultState.center
		openMapAt(center, MAP_ZOOM_OVERVIEW)
	}

	// Клик по карте модалки. Метку ставим сразу: она не должна зависеть от геокодера. Без ключа карт
	// (или при исчерпанном лимите) геокодер падает — тогда вместо адреса подставляем координаты,
	// метка и расчёт ближайшего pos всё равно отрабатывают.
	async function pickMapPoint(latitude, longitude) {
		setNewAddress((prev) => ({ ...prev, 'latitude': latitude, 'longitude': longitude }))

		const coordsText = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
		let found = null
		try {
			found = await yandexReverseGeocode(mapToken, latitude, longitude)
		} catch (e) {
			// геокодер недоступен — остаются координаты
		}

		pickMapAddress({
			'latitude': latitude,
			'longitude': longitude,
			'full_address': found?.full_address || coordsText,
			'short_address': found?.short_address || coordsText,
		})
	}

	// Адрес на карте модалки выбран (кликом или из поиска): метка, ближайший pos,
	// а для найденного поиском — ещё и центр карты, чтобы метка была видна.
	function pickMapAddress(picked, center = false) {
		setNewAddress((prev) => ({ ...prev, 'latitude': picked.latitude, 'longitude': picked.longitude }))
		findClosestPos({ ...picked, 'address': picked.full_address })
		if (center && typeof mapRef.current?.setCenter === 'function') {
			mapRef.current.setCenter([picked.latitude, picked.longitude], MAP_ZOOM_ADDRESS)
		}
	}

	function isDisable() {
		if (!data.products.length) {
			return true;
		}

		if (!data.pos_id) {
			return true;
		}

		if (Number(data.distance) > Number(data.delivery_distance)) {
			return true;
		}

		return false
	}

	function toggleModalComment(bool, type = null) {
		setComments(commentsDuplicate)
		setModalComment({ ...modalComment, 'bool': bool, 'type': type })
	}

	async function getData() {
		var menu = reduxDelivery.menu
		var poses = reduxDelivery.poses

		if (reduxDelivery?.comments?.length) {
			setComments(reduxDelivery.comments)
			setCommentsDuplicate(reduxDelivery.comments)
		}

		if (poses.length > 0 && poses[0]['longitude']) {
			setDefaultState({
				center: [poses[0]['latitude'], poses[0]['longitude']],
				zoom: 13,
				controls: ["zoomControl"]
			})
		}

		setMenu(menu)
		setMenuDuplicate(menu)

		if (cheque_id) {
			var response = await GET(`/cheques/${cheque_id}`)
			if (httpOk(response)) {
				setData({
					...data,
					...response.data.data
				})

				var responseClient = await GET(`/search-client/${response.data.data.client_phone}`, {}, { loader: true })
				if (httpOk(responseClient)) {
					responseClient = responseClient.data
					if (responseClient.id) {
						setClient({
							...client,
							'id': responseClient.id,
							'phone1': data.client_phone,
							'phone2': responseClient.phone2,
							'first_name': responseClient.first_name,
							'client_phone': data.client_phone,
							'client_phone2': responseClient.phone2,
							'client_name': responseClient.client_name,
							'note': responseClient.note,
							'gender': responseClient.gender,
							'birthday': responseClient.birthday,
							'address_id': null,
							'addresses': responseClient?.addresses,
							'loyalty_balance': responseClient.loyalty_balance,
							'telegram_user_id': responseClient.telegram_user_id,
							'count_cheques': responseClient.count_cheques,
							'exist': true,
						})
					}
				}
			}
		}
	}

	async function generatePromocode(item) {
		var sendData = {
			'id': item.id,
			'client_id': client.id,
		}

		await POST('/discount/promo-codes/generate', sendData, { 'loader': true })
	}

	async function sendSMS(item) {
		var sendData = {
			'id': item.id,
			'client_id': client.id,
		}

		await POST('/sms/send-template', sendData, { 'loader': true })
	}

	// Mison SMS: шаблон каталога вместо своего шаблона заведения
	async function sendCatalogSMS(e) {
		e.preventDefault()
		if (!client?.id || !smsCatalog.template_code) return

		const response = await POST('/sms/send-template', {
			'client_id': client.id,
			'template_code': smsCatalog.template_code,
			'vars': smsCatalog.vars,
			'locale': smsCatalog.locale,
		}, { 'loader': true })

		if (httpOk(response)) setSmsCatalog({ template_code: null, vars: {}, locale: 'auto' })
	}

	function returnRole(role) {
		if (role === 'ROLE_OPERATOR')
			return t('call_center')

		if (role === 'ROLE_COURIER')
			return t('courier')

		if (role === 'ROLE_CASHIER')
			return t('cashier')
	}

	function toggleModalModifiers(bool = false) {
		if (!bool) setModalModifiersForced(false)
		setModalModifiers(bool)
	}

	// Группы модификаторов позиции. У строки чека их может не быть (пришла из
	// сохранённого заказа), поэтому добираем из меню — и по товару, и по размеру.
	function findMenuProductById(productId, nodes = menuDuplicate) {
		if (productId == null) return null

		for (const node of nodes || []) {
			for (const product of node.products || []) {
				if (Number(product.product_id) === Number(productId)) return product
				const size = (product.sizes || []).find((x) => Number(x.product_id) === Number(productId))
				if (size) return size
			}
			const found = findMenuProductById(productId, node.children)
			if (found) return found
		}

		return null
	}

	function resolveProductModifiers(selected) {
		if (!selected) return []

		if (Array.isArray(selected.modifiers) && selected.modifiers.length > 0) {
			return selected.modifiers
		}

		const catalogProduct = findMenuProductById(selected.product_id)
		if (Array.isArray(catalogProduct?.modifiers) && catalogProduct.modifiers.length > 0) {
			return catalogProduct.modifiers
		}

		return []
	}

	// Родитель строки-модификатора: у только что добавленной связь идёт по
	// parent_transaction_id, у загруженной из чека — по parent_cheque_product_id.
	function resolveModifierLineParent(line, chequeProducts = data?.products) {
		const list = chequeProducts || []
		if (!line) return null

		if (line.parent_transaction_id) {
			const byTransaction = list.find((product) => (
				!isModifierLine(product) && product.transaction_id === line.parent_transaction_id
			))
			if (byTransaction) return byTransaction
		}

		if (line.parent_cheque_product_id !== null && line.parent_cheque_product_id !== undefined) {
			const byLine = list.find((product) => (
				!isModifierLine(product) && Number(product.id) === Number(line.parent_cheque_product_id)
			))
			if (byLine) return byLine
		}

		if (line.product_parent_id !== null && line.product_parent_id !== undefined) {
			const byProduct = list.find((product) => (
				!isModifierLine(product)
				&& Number(product.status) !== -1
				&& Number(product.product_id) === Number(line.product_parent_id)
			))
			if (byProduct) return byProduct
		}

		return null
	}

	// Группа модификатора: modifier_parent_id есть только у строк текущей сессии,
	// у загруженных из чека ищем группу по самому модификатору.
	function resolveModifierLineGroup(line, parentProduct) {
		const groups = resolveProductModifiers(parentProduct)
		if (!groups.length) return null

		if (line.modifier_parent_id !== null && line.modifier_parent_id !== undefined) {
			const byId = groups.find((group) => getModifierGroupId(group) === Number(line.modifier_parent_id))
			if (byId) return byId
		}

		return groups.find((group) => (group.products || []).some((product) => (
			Number(product.product_id) === Number(line.product_id)
		))) || null
	}

	function isModifierLineOfParent(line, parentProduct) {
		if (!parentProduct) return false

		if (line.parent_transaction_id && parentProduct.transaction_id) {
			return line.parent_transaction_id === parentProduct.transaction_id
		}

		if (
			line.parent_cheque_product_id !== null && line.parent_cheque_product_id !== undefined
			&& parentProduct.id
		) {
			return Number(line.parent_cheque_product_id) === Number(parentProduct.id)
		}

		if (line.product_parent_id !== null && line.product_parent_id !== undefined) {
			return Number(line.product_parent_id) === Number(parentProduct.product_id)
		}

		return false
	}

	/**
	 * Сколько модификаторов группы стоит у позиции (и новых, и сохранённых).
	 * onlyDraft — считать только несохранённые строки: снять в модалке можно лишь их,
	 * ушедшую в заказ строку удаляют крестиком в списке.
	 */
	function countModifierGroupQuantity(chequeProducts, parentProduct, group, onlyDraft = false) {
		const groupId = getModifierGroupId(group)
		const groupProductIds = new Set((group?.products || []).map((product) => Number(product.product_id)))
		let quantity = 0

		for (const line of chequeProducts || []) {
			if (!isModifierLine(line)) continue
			if (!isActiveChequeLine(line)) continue
			if (!isModifierLineOfParent(line, parentProduct)) continue
			if (onlyDraft && line.id) continue

			const sameGroup = (line.modifier_parent_id !== null && line.modifier_parent_id !== undefined)
				? Number(line.modifier_parent_id) === groupId
				: groupProductIds.has(Number(line.product_id))
			if (!sameGroup) continue

			quantity += Number(line.quantity || 0)
		}

		return quantity
	}

	/** Сколько штук конкретного варианта стоит у позиции. */
	function countModifierVariantQuantity(chequeProducts, parentProduct, group, variantProductId, onlyDraft = false) {
		const groupId = getModifierGroupId(group)
		let quantity = 0

		for (const line of chequeProducts || []) {
			if (!isModifierLine(line)) continue
			if (!isActiveChequeLine(line)) continue
			if (!isModifierLineOfParent(line, parentProduct)) continue
			if (Number(line.product_id) !== Number(variantProductId)) continue
			if (onlyDraft && line.id) continue
			if (
				line.modifier_parent_id !== null && line.modifier_parent_id !== undefined
				&& Number(line.modifier_parent_id) !== groupId
			) continue

			quantity += Number(line.quantity || 0)
		}

		return quantity
	}

	/** Невыполненные обязательные / min группы у позиции. */
	function getUnmetRequiredModifierGroups(product, chequeProducts, groups) {
		if (!product || isModifierLine(product)) return []
		// Удалённую строку не проверяем — её в заказе уже нет.
		if (!isActiveChequeLine(product)) return []

		const list = Array.isArray(groups) ? groups : resolveProductModifiers(product)
		const unmet = []

		for (const group of list) {
			const need = getModifierGroupNeedQty(group)
			if (need <= 0) continue
			// Группа без вариантов — недонастройка меню; блокировать заказ ею нельзя
			if (!(group.products || []).length) continue

			const quantity = countModifierGroupQuantity(chequeProducts, product, group)
			if (quantity < need) {
				unmet.push({
					'groupId': getModifierGroupId(group),
					'groupName': group.name || '',
					'min': need,
					'selected': quantity,
					'productName': product.name || '',
				})
			}
		}

		return unmet
	}

	// Обязательный модификатор нельзя снять отдельно от блюда: иначе блюдо уедет
	// на кухню недоукомплектованным. Считаем, упадёт ли группа ниже минимума,
	// если убрать из строки removedQuantity (по умолчанию — всю строку).
	function breaksRequiredModifierGroup(line, removedQuantity = null, chequeProducts = data?.products) {
		if (!line || !isModifierLine(line)) return false
		if (Number(line.status) === -1) return false

		const list = chequeProducts || []
		const parentProduct = resolveModifierLineParent(line, list)
		if (!parentProduct) return false

		const group = resolveModifierLineGroup(line, parentProduct)
		const need = getModifierGroupNeedQty(group)
		if (need <= 0) return false

		const groupQuantity = countModifierGroupQuantity(list, parentProduct, group)
		const removed = removedQuantity === null || removedQuantity === undefined
			? Number(line.quantity || 0)
			: Number(removedQuantity)

		return (groupQuantity - removed) < need
	}

	function notifyRequiredModifierLocked() {
		notify(t('modifier_required_delete_with_product'))
	}

	function resolveModifiersParentProduct(selected = data?.selectedProduct) {
		if (!selected) return null
		if (isModifierLine(selected)) return resolveModifierLineParent(selected, data?.products)
		return selected
	}

	/** Строка-модификатор в заказе: та же форма, что создаёт addToList. */
	function buildModifierLine(parent, group, variant, quantity = 1) {
		const lineQuantity = Math.max(1, Number(quantity) || 1)

		return {
			...variant,
			'product_parent_id': variant.product_parent_id ?? parent.product_id,
			'modifier_parent_id': variant.modifier_parent_id ?? getModifierGroupId(group),
			'quantity': lineQuantity,
			'status': 0,
			'selected': false,
			'parent_transaction_id': parent.transaction_id,
			'parent_cheque_product_id': parent.id ?? null,
			'transaction_id': uuidv4(),
			'total_price': Number(variant.sale_price || 0) * lineQuantity,
		}
	}

	/** Позиция строки-модификатора: сразу под блюдом, после уже стоящих модификаторов. */
	function insertModifierLine(chequeProducts, parent, parentIndex, line) {
		const siblings = chequeProducts.filter((product) => (
			isModifierLine(product)
			&& product.parent_transaction_id === parent.transaction_id
			&& isActiveChequeLine(product)
		))

		chequeProducts.splice(parentIndex + siblings.length + 1, 0, line)
	}

	/**
	 * Модификаторы «по умолчанию» из кабинета встают в заказ сразу при добавлении блюда:
	 * оператору остаётся подтвердить или поменять выбор в модалке.
	 */
	function applyDefaultModifiers(dataCopy, parentProduct) {
		if (!parentProduct || isModifierLine(parentProduct)) return 0

		const groups = resolveProductModifiers(parentProduct)
		if (!groups.length) return 0

		const parentIndex = dataCopy.products.findIndex((line) => (
			parentProduct.transaction_id && line.transaction_id === parentProduct.transaction_id
		))
		if (parentIndex === -1) return 0

		const parent = dataCopy.products[parentIndex]
		if (!parent.transaction_id) parent.transaction_id = uuidv4()

		let added = 0

		for (const group of groups) {
			const groupMax = getModifierGroupMaxQty(group)
			let taken = countModifierGroupQuantity(dataCopy.products, parent, group)

			for (const variant of group.products || []) {
				const wanted = normalizeModifierLimit(variant?.default_quantity)
				if (wanted <= 0) continue
				// Вариант в стоп-листе подставлять нельзя — оператор выберет замену руками
				const stopList = withStopListOnItem(variant, dataCopy.pos_id)
				if (stopList.stop_list_quantity !== null && Number(stopList.stop_list_quantity) === 0) continue

				const quantity = groupMax > 0 ? Math.min(wanted, groupMax - taken) : wanted
				if (quantity <= 0) break

				insertModifierLine(dataCopy.products, parent, parentIndex, buildModifierLine(parent, group, variant, quantity))
				taken += quantity
				added += 1
			}
		}

		return added
	}

	/**
	 * Модалка после добавления блюда: открываем и когда обязательная группа не закрыта,
	 * и когда подставились дефолты — оператор должен увидеть, что уедет на кухню.
	 */
	function openModifiersAfterAdd(product, chequeProducts) {
		if (!product || isModifierLine(product)) return

		const groups = resolveProductModifiers(product)
		if (!groups.length) return

		const unmet = getUnmetRequiredModifierGroups(product, chequeProducts, groups)
		if (!unmet.length && !hasDefaultModifiers(groups)) return

		setModifiers(withStopListOnModifiers(groups))
		// Пока обязательная группа пуста, модалку не закрыть; с дефолтами — просто подтверждение
		setModalModifiersForced(unmet.length > 0)
		setModalModifiers(true)
	}

	/**
	 * Выбор варианта в модалке. В группе-переключателе (max = 1) новый вариант вытесняет
	 * старый: иначе после подстановки дефолта оператор не может поменять колу на пепси.
	 */
	function chooseModifier(group, variant, parentProductId) {
		const payload = {
			...variant,
			'product_parent_id': variant.product_parent_id ?? parentProductId,
			'modifier_parent_id': variant.modifier_parent_id ?? getModifierGroupId(group),
		}

		const groupMax = getModifierGroupMaxQty(group)
		const dataCopy = JSON.parse(JSON.stringify(data))
		const parentIndex = dataCopy.products.findIndex((line) => (
			!isModifierLine(line)
			&& Number(line.product_id) === Number(parentProductId)
			&& isActiveChequeLine(line)
		))
		if (parentIndex === -1) {
			addToList(payload)
			return
		}

		const parent = dataCopy.products[parentIndex]
		const groupQuantity = countModifierGroupQuantity(dataCopy.products, parent, group)

		// Место в группе ещё есть — обычное добавление со всеми проверками addToList
		if (!isModifierSlotGroup(group) || groupMax === 0 || groupQuantity < groupMax) {
			addToList(payload)
			return
		}

		const groupLines = dataCopy.products.filter((line) => (
			isModifierLine(line)
			&& isActiveChequeLine(line)
			&& isModifierLineOfParent(line, parent)
			&& Number(line.modifier_parent_id) === getModifierGroupId(group)
		))

		if (!groupLines.length) {
			addToList(payload)
			return
		}

		// Повторный клик по единственному выбранному варианту ничего не меняет
		if (groupLines.length === 1 && Number(groupLines[0].product_id) === Number(variant.product_id)) return

		// Уже сохранённую строку так не подменить — её снимают крестиком
		if (groupLines.some((line) => line.id)) {
			notify(t('modifier_required_delete_with_product'))
			return
		}

		// Слот заполнен: новый вариант занимает его целиком («фри ×2» → «крылышки ×2»)
		dataCopy.products = dataCopy.products.filter((line) => !groupLines.includes(line))
		insertModifierLine(
			dataCopy.products,
			parent,
			dataCopy.products.indexOf(parent),
			buildModifierLine(parent, group, payload, groupMax)
		)
		calculateTotalAmount(dataCopy)
	}

	/** Снять одну штуку варианта (или всю группу при variant = null) — только несохранённые строки. */
	function removeModifier(group, variant, parentProductId, all = false) {
		const dataCopy = JSON.parse(JSON.stringify(data))
		const parentIndex = dataCopy.products.findIndex((line) => (
			!isModifierLine(line)
			&& Number(line.product_id) === Number(parentProductId)
			&& isActiveChequeLine(line)
		))
		if (parentIndex === -1) return

		const parent = dataCopy.products[parentIndex]
		const groupId = getModifierGroupId(group)
		const indexes = []

		dataCopy.products.forEach((line, index) => {
			if (!isModifierLine(line) || !isActiveChequeLine(line) || line.id) return
			if (!isModifierLineOfParent(line, parent)) return
			if (
				line.modifier_parent_id !== null && line.modifier_parent_id !== undefined
				&& Number(line.modifier_parent_id) !== groupId
			) return
			if (variant && Number(line.product_id) !== Number(variant.product_id)) return
			indexes.push(index)
		})
		if (!indexes.length) return

		if (all) {
			dataCopy.products = dataCopy.products.filter((line, index) => !indexes.includes(index))
		} else {
			const index = indexes[indexes.length - 1]
			const line = dataCopy.products[index]
			if (Number(line.quantity || 0) > 1) {
				line.quantity = Number(line.quantity) - 1
				line.total_price = Number(line.sale_price || 0) * Number(line.quantity)
			} else {
				dataCopy.products.splice(index, 1)
			}
		}

		// Выделение возвращаем на саму позицию — по нему addToList ищет родителя
		const nextParentIndex = dataCopy.products.indexOf(parent)
		dataCopy.products.forEach((line) => { line.selected = false })
		if (nextParentIndex >= 0) {
			dataCopy.products[nextParentIndex].selected = true
			dataCopy.selectedProduct = dataCopy.products[nextParentIndex]
			dataCopy.selectedIndex = nextParentIndex
		} else {
			dataCopy.selectedProduct = undefined
			dataCopy.selectedIndex = undefined
		}

		calculateTotalAmount(dataCopy)
	}

	function canCloseRequiredModifiersModal(chequeProducts = data?.products) {
		if (!modalModifiersForced) return true
		const parentProduct = resolveModifiersParentProduct()
		if (!parentProduct) return true
		const groups = resolveProductModifiers(parentProduct)
		return getUnmetRequiredModifierGroups(parentProduct, chequeProducts, groups).length === 0
	}

	function tryCloseModifiersModal() {
		if (!canCloseRequiredModifiersModal()) {
			const parentProduct = resolveModifiersParentProduct()
			const unmet = getUnmetRequiredModifierGroups(
				parentProduct,
				data?.products,
				resolveProductModifiers(parentProduct)
			)
			notify(t('modifier_group_min_required', {
				'product': unmet[0]?.productName || parentProduct?.name || '',
				'group': unmet[0]?.groupName || '',
				'min': unmet[0]?.min || 1,
			}))
			return
		}

		setModalModifiersForced(false)
		setModalModifiers(false)
	}

	// Крестик в принудительной модалке = отмена блюда. Иначе тупик: если все
	// модификаторы обязательной группы в стоп-листе, выбрать нечего, а блюдо
	// без модификатора уходить на кухню не должно.
	function cancelForcedModifiersProduct() {
		const parentProduct = resolveModifiersParentProduct()

		setModalModifiersForced(false)
		setModalModifiers(false)

		if (!parentProduct) return

		// Строку, уже сохранённую в заказе, отсюда не убрать — её удаляют кнопкой «×»
		if (parentProduct.id) {
			notify(t('modifier_required_delete_product_manually', { 'product': parentProduct.name ?? '' }))
			return
		}

		var dataCopy = { ...data }

		// selectedProduct — не обязательно тот же объект, что строка в products:
		// заказ не раз проходит через JSON-клон, поэтому ищем строку по transaction_id
		const parentIndex = dataCopy.products.findIndex((item) => (
			parentProduct.transaction_id
				? item.transaction_id === parentProduct.transaction_id
				: item === parentProduct
		))
		if (parentIndex < 0) return

		const parentLine = dataCopy.products[parentIndex]
		const parentProductId = parentLine.product_id
		const parentTransactionId = parentLine.transaction_id

		dataCopy.products = dataCopy.products.filter((item, index) => {
			if (index === parentIndex) return false
			if (!isModifierLine(item)) return true
			// без transaction_id у строки остаётся только связь по товару-родителю
			if (parentTransactionId) return item.parent_transaction_id !== parentTransactionId
			return Number(item.product_parent_id) !== Number(parentProductId)
		})

		dataCopy.selectedProduct = undefined
		dataCopy.selectedIndex = undefined
		calculateTotalAmount(dataCopy)

		notify(t('modifier_required_product_removed', { 'product': parentProduct.name ?? '' }))
	}

	// Закрытие крестиком: требования выполнены — просто закрываем, нет — отменяем блюдо
	function handleCloseModifiersModal() {
		if (canCloseRequiredModifiersModal()) {
			setModalModifiersForced(false)
			setModalModifiers(false)
			return
		}

		cancelForcedModifiersProduct()
	}

	function toggleModalClient(bool = false, item = {}) {
		setModalClient(bool)
		setSelectedItem(item)
		setClientBirthdayInitial(item?.birthday ?? null)
	}

	function toggleModalAddressDelete(bool = false, address_id = null) {
		setModalAddressDelete({ 'bool': bool, 'address_id': address_id })
	}

	function handleClientChange(e) {
		var selectedItemCopy = { ...selectedItem }
		selectedItemCopy[e.target.name] = e.target.value
		setSelectedItem(selectedItemCopy)
	}

	async function updateClient(e) {
		e.preventDefault();

		var response = await PUT(`/delivery/clients/${data.client_id}`, selectedItem, { loader: true });

		if (httpOk(response)) {
			toggleModalClient()

			setClient({
				...client,
				'phone2': selectedItem.phone2,
				'first_name': selectedItem.first_name,
				'gender': selectedItem.gender,
				'birthday': selectedItem.birthday,
				'note': selectedItem.note,
			})
		}
	}

	async function deleteClientAddress() {
		var sendData = {
			'client_id': client.id,
			'address_id': modalAddressDelete.address_id,
		}

		var response = await DELETE('/clients-address', sendData, { 'loader': true })
		if (httpOk(response)) {
			toggleModalAddressDelete(false)

			var clientCopy = { ...client }
			var index = clientCopy.addresses.findIndex(x => x.id === modalAddressDelete.address_id)
			if (index !== -1 && index !== undefined) {
				clientCopy.addresses.splice(index, 1)
				setClient(clientCopy)
			}
		}
	}

	async function toggleModalCheques(bool = false) {
		if (bool) {
			if (!client?.id) return

			var filterDataCopy = { ...filterData }
			var filterUrl = "/cheques"
			var urlParams = ""

			urlParams += returnSign(urlParams) + 'client_id=' + client.id
			if (filterDataCopy.status)
				urlParams += returnSign(urlParams) + 'status=' + filterDataCopy.status
			if (filterDataCopy.start_date)
				urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
			if (filterDataCopy.end_date)
				urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
			if (filterDataCopy.search)
				urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
			if (filterDataCopy.page)
				urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page
			filterUrl = filterUrl + urlParams

			const response = await GET(filterUrl, {}, { loader: true })
			if (httpOk(response)) {
				setCheques(response.data)
				setPagination({ url: filterUrl, response: response.data })
			}
			setModalCheques(true)
		} else {
			setModalCheques(false)
			setCheques([])
			setPagination({ url: '/cheques', response: null })
			setFilterData(prev => ({
				...prev,
				search: '',
				page: null,
			}))
		}
	}

	function paginateCheques(page) {
		setFilterData(prev => ({ ...prev, page }))
	}

	async function toggleRightbar(boolean = false, item, index) {
		var dataCopy = [...cheques.data]

		for (let i = 0; i < dataCopy.length; i++) {
			dataCopy[i]['showSidebar'] = false
		}
		if (boolean) {
			const response = await GET(`/cheques/${item.id}`, {}, { loader: true })
			if (httpOk(response)) {
				dataCopy[index] = response.data.data
				dataCopy[index]['showSidebar'] = boolean

				setSelectedItem(dataCopy[index])
				setShowRightBar(boolean)
			}

		} else {
			setShowRightBar(boolean)
			setSelectedItem({})
		}
	}

	function returnClassStatus(order_type) {
		if (order_type === 1) {
			return "bg-primary"
		}
		if (order_type === 2) {
			return "bg-success"
		}
		if (order_type === 3) {
			return "bg-warning"
		}
	}

	async function handleSelectTab(tabNumber) {
		var response
		if (tabNumber === 2) {
			response = await POST('/delivery/client/cheques', {
				'client_id': client.id
			}, { loader: true });

			if (httpOk(response)) {
				setCheques(response.data)
			}
		}

		if (tabNumber === 6) {
			response = await GET('/helper/discount-promocodes')
			if (httpOk(response)) setPromocodes(response.data)
		}

		if (tabNumber === 7) {
			response = await GET('/helper/sms-templates?sms_type_id=3')
			if (httpOk(response)) setSmsTemplates(response.data)
		}

		if (tabNumber === 8) {
			const response = await GET("/helper/products-stop-list-poses", {}, { loader: true })
			if (httpOk(response)) {
				const poses = Array.isArray(response.data) ? response.data : []
				const defaultPosId = data.pos_id || reduxAccount?.pos?.id
				let activeIndex = poses.findIndex(item => Number(item.id) === Number(defaultPosId))
				if (activeIndex < 0) activeIndex = poses.length ? 0 : -1

				const posesCopy = poses.map((item, index) => ({
					...item,
					active: index === activeIndex,
				}))
				setStopListPoses(posesCopy)
				setStopListProducts(activeIndex >= 0 ? (posesCopy[activeIndex].products || []) : [])
				setStopListSearch('')
			}
		}

		setData({ ...data, 'active_tab': tabNumber })
	}

	function handleStopListPos(index) {
		setStopListSearch('')
		setStopListProducts(stopListPoses[index]['products'] || [])

		var stopListPosesCopy = [...stopListPoses]
		for (let i = 0; i < stopListPosesCopy.length; i++) {
			stopListPosesCopy[i]['active'] = false
		}
		stopListPosesCopy[index]['active'] = true
		setStopListPoses(stopListPosesCopy)
	}

	function returnActiveTabStyle(number) {
		if (number === 1) {
			if (data.active_tab === 1) {
				return 'active'
			}
		}

		if (number === 2) {
			if (data.active_tab === 2 && client.id) {
				return 'active'
			}
			if (data.active_tab !== 2 && client.id) {
				return ''
			}
			if (data.active_tab !== 2 && !client.id) {
				return 'disabled'
			}
		}

		if (number === 8 || number === 9) {
			if (data.active_tab === number) {
				return 'active'
			}
		}

	}

	function repeatOrder(item) {
		var dataCopy = { ...data }

		dataCopy.products = []
		for (let i = 0; i < item?.products?.length; i++) {
			var found = reduxDelivery.products.find(p => p.product_id === item.products[i]['product_id']);
			found.status = 0
			found.quantity = item.products[i]['quantity']
			dataCopy.products.push(found)
		}

		dataCopy.active_tab = 1
		calculateTotalAmount(dataCopy)
	}

	function handleClickReady() {
		var dataCopy = { ...data }

		dataCopy.products[dataCopy.selectedIndex]['done'] = true
		dataCopy.products[dataCopy.selectedIndex]['selected'] = false
		dataCopy.selectedProduct = null
		dataCopy.selectedIndex = null

		setData(dataCopy)
	}

	function handleChangeDeliveryPrice(e) {
		var dataCopy = { ...data }
		if (e.target.value === "") dataCopy.delivery_price = 0

		if (Number(e.target.value.replace(/[^0-9.]/g, '')) > 100000) return

		dataCopy.delivery_price = e.target.value.replace(/[^0-9.]/g, '')
		calculateTotalAmount(dataCopy)
	}

	async function toggleModalExternalCourier(bool = false) {
		if (data.cheque_id) return

		if (bool) {
			const coordinates = getExternalCourierCoordinates();
			if (!coordinates) return;

			const response = await POST('/delivery/external-couriers/calculate', coordinates, { loader: true });
			if (httpOk(response)) {
				const responseCouriers = Array.isArray(response.data)
					? response.data
					: (response.data?.data ?? response.data?.external_couriers ?? []);
				const couriers = responseCouriers.map((item) => {
					const id = item?.id ?? item?.external_courier_id;
					const courierFromSettings = externalCouriers.find(courier => courier.id === id) ?? {};
					return {
						...courierFromSettings,
						...item,
						id,
						name: item?.name ?? courierFromSettings?.name ?? '',
						delivery_price: item?.delivery_price ?? courierFromSettings?.delivery_price ?? null,
						isUnavailable: item?.error === true || item?.success === false,
					};
				});
				setCalculatedExternalCouriers(couriers);
			} else {
				setCalculatedExternalCouriers([]);
			}
		}

		if (!bool) {
			setCalculatedExternalCouriers([]);
		}

		setModalExternalCourier(bool)
	}

	async function handleClickExternalCourier(item) {
		if (item?.isUnavailable) return

		var dataCopy = { ...data }
		dataCopy.external_courier_id = item.id
		dataCopy.external_courier_name = item.name

		const calculatedExternalPrice = item?.delivery_price
		dataCopy.use_external_delivery_price = calculatedExternalPrice !== null && !Number.isNaN(Number(calculatedExternalPrice))
		if (dataCopy.use_external_delivery_price) {
			dataCopy.external_delivery_price = Number(calculatedExternalPrice)
		}

		if (item?.is_delivery_price_override) {
			const overridePrice = item?.delivery_price
			if (overridePrice !== null && !Number.isNaN(Number(overridePrice))) {
				dataCopy.external_delivery_price = Number(overridePrice)
			}
		} else {
			if (!dataCopy.use_external_delivery_price) {
				dataCopy.external_delivery_price = null
			}
		}

		calculateTotalAmount(dataCopy)
		toggleModalExternalCourier()
	}

	function toggleModalExternalMenu(bool = false) {
		setModalExternalMenu(bool)
	}

	async function handleClickExternalMenu(item) {
		if (!item.id) {
			setMenu(reduxDelivery.menu)
			setProducts(reduxDelivery.menu)
			setSelectedMenu({})
			toggleModalExternalMenu()
			return
		}

		setMenu(item.menu)
		setProducts(item.products)
		setSelectedMenu({})
		toggleModalExternalMenu()
	}

	async function getStopList() {
		var response = await GET('/helper/products-stop-list')
		if (httpOk(response)) {
			stopList.current = response.data
			return response.data
		}
		return stopList.current
	}

	useEffect(() => {
		getData()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (cheque_id) getDeletionSettings()
	}, [cheque_id]) // eslint-disable-line react-hooks/exhaustive-deps

	// Витрина колл-центра по умолчанию (Внешние сервисы → Колл-центр → Внешнее меню).
	// Основное меню уже стоит из getData, поэтому подменяем только выбранное внешнее.
	useEffect(() => {
		if (defaultExternalMenuAppliedRef.current) return
		if (!reduxDelivery?.externalMenu?.length) return

		defaultExternalMenuAppliedRef.current = true

		const defaultExternalMenu = reduxDelivery.externalMenu.find(item => item.id && item.is_default)
		if (!defaultExternalMenu) return

		setMenu(defaultExternalMenu.menu)
		setProducts(defaultExternalMenu.products)
	}, [reduxDelivery?.externalMenu])

	useEffect(() => {
		if (!reduxDelivery?.comments?.length) return
		setCommentsDuplicate(reduxDelivery.comments)
		setComments((prev) => (prev?.length ? prev : reduxDelivery.comments))
	}, [reduxDelivery?.comments])

	useEffect(() => {
		if (!Array.isArray(liveStopList)) return
		stopList.current = liveStopList
	}, [liveStopList])

	useEffect(() => {
		orderCreatePageMountedAtRef.current = Date.now()
	}, [])

	useEffect(() => {
		if (cheque_id) return
		if (!liveTelephony) return
		if (
			typeof liveTelephony.receivedAt !== 'number' ||
			liveTelephony.receivedAt < orderCreatePageMountedAtRef.current
		) {
			return
		}

		if (reduxAccount.id === liveTelephony.user_id) {
			setData(prev => ({
				...prev,
				client_phone: liveTelephony.phone,
			}))
		}
	}, [liveTelephony, cheque_id]) // eslint-disable-line react-hooks/exhaustive-deps

	// Новый заказ из окна «Дни рождения»: /delivery/create?phone=998… — номер подставляем,
	// гостя и его адреса подтянет эффект ниже, как при наборе номера руками
	useEffect(() => {
		if (cheque_id) return
		const phone = (new URLSearchParams(window.location.search).get('phone') || '').replace(/\D/g, '')
		if (phone.length === 12) {
			setData(prev => ({ ...prev, client_phone: phone }))
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (data?.client_phone?.length === 12 && !cheque_id) {
			searchClient()
		}
	}, [data.client_phone]) // eslint-disable-line react-hooks/exhaustive-deps

	useDidMountEffect(() => {
		if (!modalCheques) return
		toggleModalCheques(true)
	}, [filterData.search, filterData.page]) // eslint-disable-line react-hooks/exhaustive-deps

	// Что осталось добрать в обязательных группах — считаем один раз на рендер
	const modifiersParentProduct = modalModifiers ? resolveModifiersParentProduct() : null
	const unmetModifierGroups = modalModifiersForced
		? getUnmetRequiredModifierGroups(modifiersParentProduct, data?.products, modifiers)
		: []

	// Блюдо-конструктор: итог и остаток бесплатного считаем по живым строкам заказа (как в кассе)
	const isConstructorModal = !!(modalModifiers && modifiersParentProduct && isConstructorDish(modifiersParentProduct))
	const modalSummary = modalModifiers && modifiersParentProduct ? summarizeConstructor(data?.products, modifiersParentProduct) : null
	const modalPortions = Math.max(1, Math.floor(Number(modifiersParentProduct?.quantity) || 1))

	return (
		<>
			<div className="waiter-footer w-100">
				<div className="waiter-footer-item waiter-footer-item--back"
					onClick={() => goTo('back')}>
					<i className="uil uil-arrow-left fz-30"></i>
				</div>
				<div className={`waiter-footer-item ${returnActiveTabStyle(1)}`}
					onClick={() => handleSelectTab(1)}>
					<span className="waiter-footer-item-text">{t('menu')}</span>
				</div>
				<div className={`waiter-footer-item ${returnActiveTabStyle(2)}`}
					onClick={() => handleSelectTab(2)}>
					<span className="waiter-footer-item-text">{t('history')}</span>
				</div>
				<div className={`waiter-footer-item ${returnActiveTabStyle(3)}`}
					onClick={() => handleSelectTab(3)}>
					<span className="waiter-footer-item-text">{t('discounts')}</span>
				</div>
				<div className={`waiter-footer-item disabled`}>
					<span className="waiter-footer-item-text">{t('payment')}</span>
				</div>
				<div className={`waiter-footer-item disabled`}>
					<span className="waiter-footer-item-text">{t('Черновик')}</span>
				</div>
				<div className={`waiter-footer-item ${client.id ? '' : 'disabled'}`}
					onClick={() => handleSelectTab(6)}>
					<span className="waiter-footer-item-text">{t('promo_code')}</span>
				</div>
				<div className={`waiter-footer-item ${client.id ? '' : 'disabled'}`}
					onClick={() => handleSelectTab(7)}>
					<span className="waiter-footer-item-text">SMS</span>
				</div>
				<div className={`waiter-footer-item ${client.id ? '' : 'disabled'}`}
					onClick={() => toggleModalCheques(true)}>
					<span className="waiter-footer-item-text">{t('cheques')}</span>
					<div className="waiter-footer-count">{formatMoney(client.count_cheques)}</div>
				</div>
				<div className={`waiter-footer-item ${returnActiveTabStyle(8)}`}
					onClick={() => handleSelectTab(8)}>
					<span className="waiter-footer-item-text">{t('stop_list')}</span>
				</div>
				<div className={`waiter-footer-item ${returnActiveTabStyle(9)}`}
					onClick={() => handleSelectTab(9)}>
					<span className="waiter-footer-item-text">{t('table_reservation')}</span>
				</div>
				<div className="waiter-footer-operator">
					<OperatorReportBlock
						chipClassName="operator-profile-chip--inline"
						onLogout={() => deliveryOutlet?.logout?.()}
					/>
				</div>
			</div>

			<div className="d-flex justify-content-center">
				{data.active_tab === 1 &&
					<>
						<div className="w-100 position-relative">
							{!data.pos_id &&
								<div className="order-blocker"></div>
							}
							<div className="waiter-header">
								<div className="header-action-wrapper" style={{ 'width': '350px' }}>
									{(
										data?.selectedProduct?.is_modifiers_enabled ||
										data?.selectedProduct?.size_parent_id
									) ?
										<>
											{!!data?.selectedProduct?.is_modifiers_enabled &&
												<div className="header-action-button header-action-button--modifiers"
													onClick={() => {
														setModifiers(withStopListOnModifiers(data?.selectedProduct?.modifiers))
														toggleModalModifiers(true)
													}}>
													<i className="uil uil-apps"></i>
													<div className="text-center">{t('modification')}</div>
												</div>
											}
											{!!data?.selectedProduct?.size_parent_id &&
												<div className="header-action-button header-action-button--sizes"
													onClick={() => changeProductSize()}>
													<i className="uil uil-layer-group"></i>
													<div className="text-center">{t('sizes')}</div>
												</div>
											}
										</>
										:
										<div className="vertical-center">
											<h4>{t('new_order')}</h4>
											{client.note &&
												<h5 className="d-flex">
													<b>{t('note')}: <span className="text-danger">{client.note}</span></b>
												</h5>
											}
										</div>
									}
								</div>
								<div className="position-relative" style={{ 'flex': 1, 'minWidth': 0 }}>
									<input type="text" className="form-control"
										id="search"
										autoComplete="off"
										style={{ 'height': '50px' }}
										value={selectedMenu?.search ?? ''}
										placeholder={t('search')}
										onChange={(e) => searchProducts(e.target.value)}
										onKeyPress={e => {
											if (e.key === 'Enter') {
												searchProductsBarcode(selectedMenu?.search)
											}
										}} />
									{!!selectedMenu?.search?.length &&
										<span className="input-inner-icon" style={{ 'right': 0, 'top': '-5px' }}
											onClick={() => searchProducts("")}>
											<i className="uil-times" style={{ 'fontSize': '40px' }}></i>
										</span>
									}
								</div>
							</div>

							<div className="waiter-body">
								<div className="position-relative" style={{ 'width': '350px', 'flexShrink': 0 }}>
									<div className="order-left" ref={scrollToBottomRef}>
										{data?.products?.map((item, index) => (
											<div className={`order-product${item.status === -1 ? ' del' : ''}${item.selected ? ' active' : ''}`} key={index}
												onClick={() => selectProduct(index)}>
												<div className="d-flex">
													<div className="order-product-quantity"
														style={isModifierLine(item) ? { 'fontStyle': 'italic' } : {}}>
														<div className="d-flex">
															{isModifierLine(item) &&
																<span>x</span>
															}
															<span>{item.quantity}</span>
														</div>
													</div>
													<div className="order-product-info">
														<div className="order-product-name">
															{item.name}
														</div>
														{item.is_promo_gift && (
															<div className="order-product-comment" style={{ color: 'var(--dark-fg-primary, #0d6efd)', fontWeight: 600 }}>
																{t('promo_gift') || 'подарок'}
															</div>
														)}
														{(item.price_kind === 'free' || item.price_kind === 'included') && (
															<div className="order-product-comment" style={{ color: '#198754', fontWeight: 600 }}>
																{item.price_kind === 'free' ? 'бесплатно' : 'входит в цену'}
															</div>
														)}
														{item.is_auto_add && !item.is_promo_gift && (
															<div className="order-product-comment" style={{ color: 'var(--dark-fg-primary, #0d6efd)', fontWeight: 600 }}>
																{t('auto_add') || 'авто'}
															</div>
														)}
														{!!item.comment && !item.is_promo_gift && (
															<div className="order-product-comment">
																{item.comment}
															</div>
														)}
														<div className="order-product-comment gap-2">
															{item?.ingredients?.map((ingredient, iIndex) => (
																<Fragment key={iIndex}>
																	{ingredient?.del &&
																		<span className="del">{ingredient.name},</span>
																	}
																</Fragment>
															))}
														</div>
													</div>
												</div>
												<div className="order-product-price">{formatMoney(item.is_promo_gift ? 0 : item.total_price)}</div>
											</div>
										))}
									</div>
									<div className="order-info-total w-100">
										<div className="d-flex justify-content-between fz-12">
											<div className="vertical-center order-info-left">{t('amount')}</div>
											<div className="vertical-center order-info-left">{formatMoney(data.total_amount)}</div>
										</div>
										<div className="d-flex justify-content-between fz-12">
											<div className="vertical-center order-info-left">{t('discount')}</div>
											<div className="vertical-center order-info-left">{formatMoney(data.discount || 0)}</div>
										</div>
										<div className="d-flex justify-content-between fz-12">
											<div className="vertical-center order-info-left">
												{t('delivery')}
												{data.delivery_zone_name ? ` · ${data.delivery_zone_name}` : ''}
												{data.delivery_minutes ? ` · ${data.delivery_minutes} ${t('min')}` : ''}
											</div>
											<div className="vertical-center order-info-left">{formatMoney(data.delivery_price)}</div>
										</div>

										<div className="d-flex justify-content-between fz-16">
											<div className="vertical-center order-info-left">{t('total')}</div>
											<div className="vertical-center order-info-left">{formatMoney(data.total_amount_service)}</div>
										</div>
									</div>
									<div className="order-action-bar w-100">
										<button className="btn-footer d-none"
											disabled
											onClick={() => { }}>
											<i className="uil uil-ellipsis-v"></i>
										</button>
										<button className="btn-footer"
											disabled={!(data.selectedIndex >= 0)}
											onClick={() => handleClickReady()}>
											<i className="uil uil-check"></i>
										</button>
										<button className="btn-footer"
											disabled={
												!(data.selectedIndex >= 0)
												|| data.selectedProduct?.is_promo_gift
												|| data.selectedProduct?.is_required
												|| data.selectedProduct?.is_auto_add
											}
											onClick={() => deleteSelectedProduct()}>
											<i className="uil uil-times"></i>
										</button>
										<button className="btn-footer"
											disabled={
												!(data.selectedIndex >= 0)
												|| data.selectedProduct?.is_promo_gift
												|| data.selectedProduct?.is_required
												|| data.selectedProduct?.is_auto_add
											}
											onClick={() => changeSelectedProductQuantity()}>
											123
										</button>
										<button className="btn-footer"
											disabled={
												!(data.selectedIndex >= 0)
												|| data.selectedProduct?.is_promo_gift
												|| data.selectedProduct?.is_required
												|| data.selectedProduct?.is_auto_add
											}
											onClick={() => instantChangeSelectedProductQuantity('-')}>
											<i className="uil uil-minus"></i>
										</button>
										<button className="btn-footer"
											disabled={
												!(data.selectedIndex >= 0)
												|| data.selectedProduct?.is_promo_gift
												|| data.selectedProduct?.is_required
												|| data.selectedProduct?.is_auto_add
											}
											onClick={() => instantChangeSelectedProductQuantity('+')}>
											<i className="uil uil-plus"></i>
										</button>
									</div>
								</div>
								{reduxAccount?.call_center?.is_image_visible &&
									<div className="order-right no-select">
										<div className="w-100 order-products-wrapper no-scroll"
											style={{ 'padding': '0 3px' }}>
											<div className="d-flex flex-wrap" style={{ 'gap': '0.15rem' }}>
												{selectedMenu?.products?.map((item, index) => (
													<div className={`order-item-box2 ${item.stop_list_quantity === 0 ? 'cursor-not-allowed opacity-60' : ''}`}
														key={item.product_id ?? index}
														data-id={item.product_id}
														onClick={() => {
															if (item?.sizes?.length) selectSize(item)
															if (!item?.sizes?.length) addToList(item)
														}}>
														<>
															<div style={{
																'backgroundImage': item.image
																	? `url(${item.image})`
																	: 'none',
																'backgroundSize': 'cover',
																'backgroundPosition': 'center',
																'width': '100%',
																'height': '100px',
															}}>
																{!item.image &&
																	<div className="vertical-center align-items-center h-100" style={{ 'color': 'var(--dark-text-strong, #000)' }}>
																		{t('image')}
																	</div>
																}
															</div>

															<div className="order-product-name2" data-id={item.product_id}
																style={{
																	'width': '100%',
																	'height': '40px',
																	'color': (item.color ? item.color : 'var(--dark-text-strong, #1e1d1d)'),
																	'backgroundColor': (item.bg_color ? item.bg_color : 'var(--dark-surface, #fff)'),
																}}>
																<span>{item.name}</span>
															</div>
															{(item.stop_list_quantity >= 0 && item.stop_list_quantity !== null) &&
																<div className="order-stoplist-info2"
																	data-id={item.product_id}>
																	{item.stop_list_quantity === 0 ?
																		<div style={{ 'color': '#fff' }}>STOP</div>
																		:
																		<div style={{ 'minWidth': '20px' }}
																			className="text-center">
																			{formatMoney(item.stop_list_quantity)}
																		</div>
																	}
																</div>
															}
															{Number(item.sale_price) > 0 &&
																<div className="order-saleprice-info2"
																	data-id={item.product_id}>
																	{formatMoney(item.sale_price)}
																</div>
															}
														</>
													</div>
												))}
											</div>
										</div>
										<div className="order-products-wrapper no-scroll"
											style={{ 'minWidth': '325px' }}>
											<Fragment>
												{menuParent.parent_id && (
													<div className="order-item-box-category"
														style={{
															'width': '100%',
															'height': '50px',
															'marginBottom': '5px',
															'color': (menuParent.color ? menuParent.color : 'var(--dark-text-strong, #1e1d1d)'),
															'backgroundColor': (menuParent.bg_color ? menuParent.bg_color : 'var(--dark-surface, #fff)'),
														}}
														onClick={() => handleBackClick()}>
														<div className="d-flex cursor">
															<i className="uil uil-angle-left fz30"></i>
															<div className="vertical-center">{t('back')}</div>
														</div>
													</div>
												)}
												<div className="d-flex flex-wrap" style={{ 'gap': '0.15rem' }}>
													{menu.map((item, index) => (
														<div className="order-item-box-category"
															style={{
																'backgroundColor': (item.bg_color ? item.bg_color : 'var(--dark-surface, #fff)'),
																'color': (item.color ? item.color : 'var(--dark-text-strong, #1e1d1d)')
															}}
															key={index}
															onClick={() => selectMenu(index)}>
															<div className="text-center ellipse-3">{item.name}</div>
														</div>
													))}
												</div>
											</Fragment>
										</div>
									</div>
								}
								{!reduxAccount?.call_center?.is_image_visible &&
									<div className="order-right no-select">
										<div className="w-100 order-products-wrapper no-scroll"
											style={{ 'padding': '0 3px' }}>
											<div className="d-flex flex-wrap pb-2"
												style={{ 'gap': '0.15rem' }}>
												{selectedMenu?.products?.map((item, index) => (
													<div className="order-item-box" key={index}
														style={{
															'backgroundColor': (item.bg_color ? item.bg_color : 'var(--dark-surface, #FFFFFF)'),
															'color': (item.color ? item.color : 'var(--dark-text-strong, #000)')
														}}
														onClick={() => {
															if (item?.sizes?.length > 0) {
																selectSize(item)
															} else {
																addToList(item)
															}
														}}>
														<div className="text-center ellipse-3">
															{item.name}
														</div>
														{(Number(item.stop_list_quantity) >= 0 && item.stop_list_quantity !== null) &&
															<div className="order-stoplist-info"
																data-id={item.product_id}>
																{Number(item.stop_list_quantity) === 0 ?
																	<div>STOP</div>
																	:
																	<div style={{ 'minWidth': '20px' }}
																		className="text-center">
																		{formatMoney(item.stop_list_quantity)}
																	</div>
																}
															</div>
														}
														{Number(item.sale_price) > 0 &&
															<div className="order-saleprice-info"
																data-id={item.product_id}>
																{formatMoney(item.sale_price)}
															</div>
														}
													</div>
												))}
											</div>
										</div>
										<div className="order-products-wrapper no-scroll"
											style={{ 'minWidth': '325px' }}>
											<Fragment>
												{menuParent.parent_id && (
													<div className="order-item-box-category"
														style={{
															'width': '100%',
															'height': '50px',
															'marginBottom': '5px',
															'color': (menuParent.color ? menuParent.color : 'var(--dark-text-strong, #1e1d1d)'),
															'backgroundColor': (menuParent.bg_color ? menuParent.bg_color : 'var(--dark-surface, #fff)'),
														}}
														onClick={() => handleBackClick()}>
														<div className="d-flex cursor">
															<i className="uil uil-angle-left fz30"></i>
															<div className="vertical-center">{t('back')}</div>
														</div>
													</div>
												)}
												<div className="d-flex flex-wrap"
													style={{ 'gap': '0.15rem' }}>
													{menu.map((item, index) => (
														<div className="order-item-box-category"
															style={{
																'backgroundColor': (item.bg_color ? item.bg_color : 'var(--dark-surface, #fff)'),
																'color': (item.color ? item.color : 'var(--dark-text-strong, #1e1d1d)')
															}}
															key={index}
															onClick={() => selectMenu(index)}>
															<div className="text-center ellipse-3">{item.name}</div>
														</div>
													))}
												</div>
											</Fragment>
										</div>
									</div>
								}
							</div>
						</div>
					</>
				}
				{data.active_tab === 3 &&
					<div className="w-100 p-3">
						{!data.pos_id ? (
							<div className="text-muted">{t('poses') || 'Выберите торговую точку'}</div>
						) : (
							<div className="d-flex flex-wrap gap-2">
								{(getDiscountsForPos(reduxDelivery?.discounts, data.pos_id).fixed || [])
									.filter((item) => matchesDiscountOrderType(
										item,
										data.order_type,
										data.order_type_sub_id
									))
									.map((item) => {
									const active = (data.discounts || []).some(
										(d) => d.entity_type === 'discount_fixed' && Number(d.entity_id) === Number(item.id)
									)
									const calculated = Number(data.total_amount || 0) * Number(item.discount_payment_value || 0) / 100
									return (
										<button
											key={item.id}
											type="button"
											className={`btn ${active ? 'btn-dark' : 'btn-outline-secondary'}`}
											style={{ width: 245, minHeight: 80 }}
											onClick={() => toggleFixedDiscount(item)}>
											<div className="d-flex flex-column gap-2 text-start p-2">
												<span>{item.name}</span>
												<b>- {formatMoney(calculated)} ({item.discount_payment_value}%)</b>
											</div>
										</button>
									)
								})}
								{!(getDiscountsForPos(reduxDelivery?.discounts, data.pos_id).fixed || [])
									.filter((item) => matchesDiscountOrderType(
										item,
										data.order_type,
										data.order_type_sub_id
									)).length && (
									<div className="text-muted">{t('no_data') || 'Нет ручных скидок'}</div>
								)}
							</div>
						)}
					</div>
				}
				{data.active_tab === 2 &&
					<div className="w-100 p-2">
						<div className="card">
							<div className="card-body">
								<div className="table-responsive">
									<table className="table table-bordered">
										<thead>
											<tr>
												<th style={{ 'width': '10%' }}>ID</th>
												<th style={{ 'width': '10%' }} className="text-center">{t('date')}</th>
												<th style={{ 'width': '30%' }}>{t('products')}</th>
												<th style={{ 'width': '30%' }}>{t('address')}</th>
												<th style={{ 'width': '10%' }} className="text-end">{t('total')}</th>
											</tr>
										</thead>
										<tbody>
											{cheques.map((item, index) => (
												<tr key={index}>
													<td>{item.id}</td>
													<td className="text-center">{formatDate(item.cheque_opened_at)}</td>
													<td>
														{item.products.map((item, index) => (
															<div key={index}>
																x{item.quantity} {item.product_name}
															</div>
														))}
													</td>
													<td>
														<div>{item.name}</div>
														<div>
															{item.apartment &&
																<span>{t('apartment')}: {item.apartment},</span>
															}
															{item.floor &&
																<span>{t('floor')}: {item.floor},</span>
															}
															{item.entrance &&
																<span>{t('entrance')}: {item.entrance},</span>
															}
															{item.intercom &&
																<span>{t('intercom')}: {item.intercom},</span>
															}
														</div>
													</td>
													<td className="text-end position-relative">
														<div style={{ 'height': '70px' }}>
															{formatMoney(item.total_amount_service)}
														</div>
														<button className="btn btn-success btn-sm w-100"
															style={{
																'position': 'absolute',
																'left': 0,
																'bottom': 0,
																'borderRadius': 0
															}}
															onClick={() => repeatOrder(item)}>
															{t('repeat')}
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				}
				{data.active_tab === 6 &&
					<div className="w-100 p-2">
						<div className="card" style={{ 'height': 'calc(100vh - 70px)' }}>
							<div className="card-body">
								<div className="d-flex flex-wrap gap-2">
									{promocodes?.map((item, index) => (
										<button type="button" className="btn-footer" key={index}
											style={{ 'width': '140px', 'height': '55px' }}
											onClick={() => generatePromocode(item)}>
											{item.name}
										</button>
									))}
								</div>
							</div>
						</div>
					</div>
				}
				{data.active_tab === 7 &&
					<div className="w-100 p-2">
						<div className="card" style={{ 'height': 'calc(100vh - 70px)', 'overflowY': 'auto' }}>
							<div className="card-body">
								{/* Mison SMS: шаблон каталога + поля, текст собирает сервер */}
								<form onSubmit={sendCatalogSMS} autoComplete="off" style={{ 'maxWidth': '640px' }}>
									<SmsCatalogPicker
										usage="callcenter,marketing"
										value={smsCatalog}
										onChange={setSmsCatalog}
										onModeChange={setIsSmsCatalog}
										exampleName={client?.first_name || null}
									/>
									{isSmsCatalog &&
										<button type="submit" className="btn btn-primary w-100"
											disabled={!client?.id || !smsCatalog.template_code}>
											{client?.id ? t('send') : t('sms_choose_client')}
										</button>
									}
								</form>
								{!isSmsCatalog && smsTemplates?.map((item, index) => (
									<button type="button" className="btn-footer" key={index}
										style={{ 'width': '140px', 'height': '55px' }}
										onClick={() => sendSMS(item)}>
										{item.name}
									</button>
								))}
							</div>
						</div>
					</div>
				}
				{data.active_tab === 8 &&
					<div className="w-100 p-2">
						<div style={{ 'height': 'calc(100vh - 70px)' }}>
							<div className="d-flex gap-2 h-100">
								<div style={{ 'width': '20%' }}>
									{stopListPoses?.map((item, index) => (
										<div className={`branch-card p-2 ${item.active ? 'active' : ''}`} key={index}
											onClick={() => handleStopListPos(index)}>
											<div className="d-flex gap-3">
												<div className="icon-box">
													<i className="uil uil-map-marker"></i>
												</div>
												<div className="fw-bold">{item.name}</div>
											</div>
										</div>
									))}
								</div>
								<div className="card" style={{ 'width': '80%' }}>
									<div className="table-responsive p-2" style={{ 'height': 'calc(100vh - 80px)', 'overflow': 'auto' }}>
										<table className="table table-bordered table-hover mb-0">
											<thead>
												<tr>
													<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
													<th className="td-input" style={{ 'width': '55%' }}>
														<input
															type="text"
															className="form-control"
															autoComplete="off"
															placeholder={t('search') + '...'}
															value={stopListSearch}
															onChange={(e) => setStopListSearch(e.target.value)}
														/>
													</th>
													<th style={{ 'width': '30%' }}>{t('employee')}</th>
													<th style={{ 'minWidth': '115px' }} className="text-center">{t('date')}</th>
													<th style={{ 'width': '15%' }} className="text-end">{t('balance2')}</th>
												</tr>
											</thead>
											<tbody>
												{stopListProducts
													.filter((item) => {
														if (!stopListSearch.trim()) return true
														return String(item.name || '')
															.toLowerCase()
															.includes(stopListSearch.trim().toLowerCase())
													})
													.map((item, index) => (
														<tr key={index}>
															<td className="text-center">{index + 1}</td>
															<td>{item.name}</td>
															<td>{item.employee_name}</td>
															<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
															<td className="text-end">{item.quantity}</td>
														</tr>
													))}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					</div>
				}
				{data.active_tab === 9 &&
					<Reservation client={client} clientPhone={data.client_phone}></Reservation>
				}
				<div className="order-client-wrapper bg-white" style={{ minWidth: '450px' }}>
					<div className="order-client-scroll px-2 py-2" style={{ height: 'calc(100vh - 90px)', 'overflow': 'auto' }}>
						<div className="delivery-client-head">
							<div className="delivery-client-head-main">
								<div className="form-group mb-2">
									<div className="position-relative">
										<input type="number" className="form-control"
											disabled={data.id}
											autoFocus
											name="client_phone"
											autoComplete="off"
											ref={refPhoneInput}
											value={data.client_phone ?? ''}
											onChange={(e) => {
												if (e.target.value.length < 13) {
													setData({ ...data, 'client_phone': e.target.value })
												}
											}}
										/>
										<button className="input-button-right delivery"
											disabled={!data.client_id}
											onClick={() => toggleModalClient(true, client)}>
											<i className="uil uil-edit-alt input-icon"></i>
										</button>
									</div>
								</div>

								{!data.cheque_id &&
									<div className="delivery-order-type-switch">
										<button
											type="button"
											className={`delivery-order-type-btn${orderType === 3 ? ' active' : ''}`}
											onClick={() => handleChangeOrderType(3)}>
											{t('delivery')}
										</button>
										<button
											type="button"
											className={`delivery-order-type-btn${orderType === 2 ? ' active' : ''}`}
											onClick={() => handleChangeOrderType(2)}>
											{t('takeaway')}
										</button>
									</div>
								}
							</div>

							<div className="delivery-client-meta">
								<div className="delivery-client-meta-row">
									<span className="delivery-client-meta-label">{t('name')}</span>
									<span className="delivery-client-meta-value">
										{client.first_name ? client.first_name : '—'}
									</span>
								</div>
								<div className="delivery-client-meta-row">
									<span className="delivery-client-meta-label">{t('loyalty')}</span>
									<span className="delivery-client-meta-value">
										{client.loyalty_balance ? formatMoney(client.loyalty_balance) : '—'}
									</span>
								</div>
								{data.client_phone?.length >= 12 ? (
									<a
										href={`https://t.me/+${String(data.client_phone).replace(/\D/g, '')}`}
										target="_blank"
										rel="noreferrer"
										className="delivery-telegram-link"
									>
										<i className="uil uil-telegram"></i>
										<span>{t('telegram_chat')}</span>
									</a>
								) : null}
							</div>
						</div>

						<div className="delivery-address-panel">
							<div className="d-flex gap-2 delivery-pos-coords-row">
								<div className="form-group mb-1 delivery-pos-coords-col">
									<label>{t('poses')}<span className="required-mark">*</span></label>
									<Select
										classNamePrefix="delivery-select"
										menuPosition="fixed"
										menuPortalTarget={document.body}
										styles={{
											menuPortal: (base) => ({ ...base, zIndex: 1060 }),
										}}
										isClearable
										isDisabled={!(client.id && !data.cheque_id)}
										options={reduxDelivery?.poses}
										value={reduxDelivery?.poses.find(option => option.id === data.pos_id) ?? null}
										onChange={(option) => handleSelectPos(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>

								{!!reduxAccount?.settings?.bool_courier_select &&
									<div className="form-group mb-1 delivery-pos-coords-col">
										<label>{t('courier')}</label>
										<Select
											classNamePrefix="delivery-select"
											menuPosition="fixed"
											menuPortalTarget={document.body}
											styles={{
												menuPortal: (base) => ({ ...base, zIndex: 1060 }),
											}}
											value={couriers.find(option => option.id === data.courier_id)}
											options={couriers}
											onChange={(option) => setData({ ...data, 'courier_id': option.id })}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								}

							</div>

							<div className="form-group mb-2">
								{/* без ключа карт поле показываем всем, но неактивным — чтобы сеть видела наличие функционала */}
								<AddressSuggestField
									mapToken={mapToken}
									boundedBy={mapSearchBounds}
									disabled={!(client.id && orderType === 3)}
									placeholder={t('search_address')}
									onPick={handleAddressSuggestPick}
									onOpenMap={() => openMapOverview()}
									openMapDisabled={!(mapToken && orderType === 3 && data.client_id)} />
							</div>

							{(!client.id && newAddress.address) &&
								<div className={`delivery-address-card${newAddress?.new_address !== t('new_address') ? ' active' : ''}`}
									onClick={() => toggleModalMap(true)}>
									<div className="delivery-address-card-text">{newAddress.new_address}</div>
									{newAddress.pos_name &&
										<b className="fz-12">{newAddress.pos_name} [{newAddress.distance}]km</b>
									}
								</div>
							}

							<div className="delivery-address-cards">
								{client?.addresses?.length > 0 &&
									client.addresses.map((item, index) => (
										<div
											key={item.id || index}
											className={`delivery-address-card${data?.address_id === item.id ? ' active' : ''}${!(client.id && orderType === 3) ? ' disabled' : ''}`}
											onClick={() => {
												if (!(client.id && orderType === 3)) return;
												if (data.address_id === item.id) {
													clearSelectedAddress(data);
													return;
												}
												if (!item.id) {
													setClient({ ...client, address_id: item.id, address: '' });
												} else {
													if (data.order_type === 3) handleSelectAddress(client, item, index);
												}
											}}>
											<button
												type="button"
												className="delivery-address-card-delete"
												disabled={!(client.id && orderType === 3)}
												onClick={(e) => {
													e.stopPropagation();
													toggleModalAddressDelete(true, item.id);
												}}>
												<i className="uil-times"></i>
											</button>
											{data?.address_id === item.id && item.distance != null && item.distance !== '' && (
												<span className="delivery-address-card-km">
													{item.distance} km
												</span>
											)}
											<div className="delivery-address-card-text" title={item.short_address}>
												{item.short_address}
											</div>
											{item.created_by && (
												<span className="delivery-address-card-role" title={item.created_by}>
													{returnRole(item.role)}
												</span>
											)}
										</div>
									))
								}
								{client?.addresses?.length === 0 &&
									[0, 1, 2, 3].map((index) => (
										<div key={index} className="delivery-address-card delivery-address-card-empty" />
									))
								}
							</div>

							<div className="delivery-address-details-row">
								<div className="form-group mb-0">
									<input type="text" className="form-control" name="apartment"
										disabled={!(client.address_id)}
										autoComplete="off"
										placeholder={t('apartment')}
										value={data.apartment ?? ''}
										onChange={(e) => setData({ ...data, 'apartment': e.target.value })}
									/>
								</div>
								<div className="form-group mb-0">
									<input type="text" className="form-control" name="floor"
										disabled={!(client.address_id)}
										autoComplete="off"
										placeholder={t('floor')}
										value={data.floor ?? ''}
										onChange={(e) => setData({ ...data, 'floor': e.target.value })}
									/>
								</div>
								<div className="form-group mb-0">
									<input type="text" className="form-control" name="entrance"
										disabled={!(client.address_id)}
										autoComplete="off"
										placeholder={t('entrance')}
										value={data.entrance ?? ''}
										onChange={(e) => setData({ ...data, 'entrance': e.target.value })}
									/>
								</div>
								<div className="form-group mb-0">
									<input type="text" className="form-control" name="intercom"
										disabled={!(client.address_id)}
										autoComplete="off"
										placeholder={t('intercom')}
										value={data.intercom ?? ''}
										onChange={(e) => setData({ ...data, 'intercom': e.target.value })}
									/>
								</div>
							</div>

							<div className="form-group mb-0 mt-1">
								<input type="text" className="form-control" name="navigate"
									disabled={!(client.id && orderType === 3)}
									autoComplete="off"
									placeholder={t('navigate_for_courier')}
									value={data.navigate ?? ''}
									onChange={(e) => setData({ ...data, 'navigate': e.target.value })}
								/>
							</div>
						</div>

						{!data.cheque_id &&
							<div className="delivery-payment-types">
								{reduxDelivery?.paymentTypes?.map((item, index) => (
									<button
										type="button"
										key={item.payment_type_id || index}
										className={`delivery-payment-type-btn${data.payment_type_id === item.payment_type_id ? ' active' : ''}`}
										style={{
											color: item.color || undefined,
											backgroundColor: item.bg_color || undefined,
										}}
										onClick={() => setData({ ...data, 'payment_type_id': item.payment_type_id })}>
										{item.name}
									</button>
								))}
							</div>
						}

						<div className="delivery-comments-row">
							<div className="form-group flex-fill mb-0">
								<div className="position-relative">
									<input type="text" className="form-control input-with-icon" name="note"
										autoComplete="off"
										placeholder={t('cheque_comment')}
										value={data.note ?? ''}
										onChange={(e) => setData({ ...data, 'note': e.target.value })}
									/>
									<button type="button" className="input-button-right delivery"
										onClick={() => toggleModalComment(true, 'note')}>
										<i className="uil uil-comment-alt-message input-icon"></i>
									</button>
								</div>
							</div>
							<div className="form-group flex-fill mb-0">
								<div className="position-relative">
									<input type="text" className="form-control input-with-icon" name="comment"
										autoComplete="off"
										placeholder={t('kitchen_comment')}
										value={data.comment ?? ''}
										onChange={(e) => setData({ ...data, 'comment': e.target.value })}
									/>
									<button type="button" className="input-button-right delivery"
										onClick={() => toggleModalComment(true, 'comment')}>
										<i className="uil uil-comment-alt-message input-icon"></i>
									</button>
								</div>
							</div>
						</div>

						{!data.cheque_id &&
							<>
								{canPreorders &&
									<>
										<div className="form-group">
											<div className="calendar-input w-100">
												<DatePicker
													showMonthDropdown
													showYearDropdown
													dropdownMode="select"
													yearDropdownItemNumber={2}
													className="form-control"
													dateFormat="dd.MM.yyyy HH:mm"
													selected={data.cheque_scheduled_at ? new Date(data.cheque_scheduled_at) : null}
													onChange={(date) => {
														let executeDate = null;
														if (date) {
															executeDate = new Date(date.getTime() - 30 * 60000); // минус 30 минут
														}

														setData({
															...data,
															cheque_scheduled_at: date ? formatDate(date, "yyyy-MM-dd HH:mm:ss") : null,
															cheque_execute_at: executeDate ? formatDate(executeDate, "yyyy-MM-dd HH:mm:ss") : null,
														});
													}}
													minDate={new Date()}
													maxDate={new Date(new Date().setDate(new Date().getDate() + 14))}
													placeholderText={t('pre_order_date')}
													showTimeSelect
													timeFormat="HH:mm"
													timeIntervals={15}
												/>
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>

										<div className="form-group">
											<div className="calendar-input w-100">
												<DatePicker
													showMonthDropdown
													showYearDropdown
													dropdownMode="select"
													yearDropdownItemNumber={2}
													className="form-control"
													dateFormat="dd.MM.yyyy HH:mm"
													selected={data.cheque_execute_at ? new Date(data.cheque_execute_at) : null}
													onChange={(date) =>
														setData({
															...data,
															'cheque_execute_at': date ? formatDate(date, "yyyy-MM-dd HH:mm:ss") : null,
														})
													}
													minDate={new Date()}
													maxDate={new Date(new Date().setDate(new Date().getDate() + 14))}
													placeholderText={t('send_kitchen_date')}
													showTimeSelect
													timeFormat="HH:mm"
													timeIntervals={15}
												/>
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
									</>
								}

								{Number(data.distance) > Number(data.delivery_distance) &&
									<div className="text-danger text-center">
										{t('maximum_delivery_distance')} {data.delivery_distance} km
									</div>
								}
							</>
						}

						{!!data.is_preorder &&
							<>
								<div className="d-flex justify-content-between">
									<div>{t('cook_now')}</div>
									<div>
										<input type="checkbox" className="form-check-input"
											checked={data.cook_now ?? false}
											onChange={(e) => setData({ ...data, 'cook_now': !data.cook_now })}
										/>
									</div>
								</div>
							</>
						}
					</div>

					<div className="footer-options">
						<div className="d-flex gap-2 px-2 py-0">
							<div className="d-flex align-items-center">
								<i className="uil uil-restaurant me-3 fz-20"></i>
								<i className="uil uil-minus cursor"
									onClick={() => {
										if (data.eating_kit > 1) {
											setData({
												...data,
												'eating_kit': data.eating_kit - 1,
												'people_number': data.eating_kit - 1
											})
										}
									}}>
								</i>
								<input type="text" className="delivery-small-input"
									value={data.eating_kit ?? ''}
									onChange={(e) => setData({
										...data,
										'eating_kit': e.target.value,
										'people_number': e.target.value
									})} />
								<i className="uil uil-plus cursor"
									onClick={() => setData({
										...data,
										'eating_kit': data.eating_kit + 1,
										'people_number': data.eating_kit + 1
									})}>
								</i>
							</div>
							<input type="text" name="delivery_price"
								style={{
									'border': '1px solid var(--dark-border, #E4E4E4)',
									'borderRadius': '6px',
									'outline': 'none',
									'textAlign': 'center',
									'width': '70px',
								}}
								placeholder={t('delivery')}
								autoComplete="off"
								disabled={!(data.order_type === 3) || data.use_external_delivery_price}
								value={data.delivery_price ?? ''}
								onChange={(e) => handleChangeDeliveryPrice(e)}
							/>

							<button type="button"
								className={`delivery-small-btn ${data?.external_courier_id ? 'active' : ''}`}
								style={{
									'fontSize': '14px',
									'lineHeight': '10px',
								}}
								disabled={!(selectedExternalCouriers?.length && !data.cheque_id && getExternalCourierCoordinates())}
								onClick={() => toggleModalExternalCourier(true)}>
								{t('external_couriers')}
							</button>

							<button type="button"
								className="delivery-small-btn"
								style={{
									'fontSize': '14px',
									'lineHeight': '10px',
								}}
								disabled={!(reduxDelivery?.externalMenu?.length)}
								onClick={() => toggleModalExternalMenu(true)}>
								{t('external_menu')}
							</button>

							<button className="delivery-small-btn"
								disabled={data.cheque_id}
								onClick={() => clearData()}>
								<i className="uil uil-trash-alt"></i>
							</button>
						</div>

						<button className="delivery-save-btn"
							disabled={isDisable()}
							onClick={() => createUpdate()}>
							{t('save')}
						</button>
					</div>
				</div>
			</div>


			{/* MODAL CHEQUES */}
			<Modal show={modalCheques} animation={false} centered size="xl"
				dialogClassName="update-modal-width operator-cheques-modal"
				onHide={() => toggleModalCheques(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('cheques')} [{cheques.total}]</Modal.Title>
				</Modal.Header>
				<Modal.Body className="operator-cheques-modal-body">
					<div className="d-flex flex-wrap align-items-end gap-2 mb-3">
						<div>
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}>
							</FilterSearch>
						</div>
						<div className="filter-block mb-0 flex-grow-1">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date &&
											<span>{formatDate(filterData.start_date)}</span>
										}
										{(filterData.start_date && filterData.end_date) &&
											<span> | </span>
										}
										{filterData.end_date &&
											<span>{formatDate(filterData.end_date)}</span>
										}
									</span>
								}
							</div>
						</div>
					</div>

					<div className="table-responsive operator-cheques-table">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'minWidth': '180px' }}>{t('pos')}</th>
									<th style={{ 'minWidth': '120px' }}>{t('cashier')}</th>
									<th style={{ 'minWidth': '120px' }}>{t('waiter')}</th>
									<th style={{ 'minWidth': '150px' }}>{t('client')}</th>
									<th style={{ 'minWidth': '130px' }}>{t('phone')}</th>
									<th style={{ 'minWidth': '80px' }} className="text-center">
										{t('cheque')} №
									</th>
									<th style={{ 'minWidth': '125px' }} className="text-center">{t('open_date')}</th>
									<th style={{ 'minWidth': '100px' }} className="text-end">{t('total')}</th>
								</tr>
							</thead>
							<tbody>
								{cheques?.data?.map((item, index) => (
									<tr className={`cursor ${item.showSidebar ? 'table-tr-active' : ''}`} key={index}>
										<td className="text-center">
											<CustomTooltip text={item.id}>
												{item.row_number}
											</CustomTooltip>
										</td>
										<td className="td-ellipsis" onClick={() => toggleRightbar(true, item, index)}>
											{item.pos_short_name}
										</td>
										<td className="td-ellipsis" onClick={() => toggleRightbar(true, item, index)}>
											{item.cashier_first_name}
										</td>
										<td className="td-ellipsis" onClick={() => toggleRightbar(true, item, index)}>
											{item.waiter_first_name}
										</td>
										<td className="td-ellipsis" onClick={() => toggleRightbar(true, item, index)}>
											{item.client_name || '—'}
										</td>
										<td className="text-nowrap" onClick={() => toggleRightbar(true, item, index)}>
											{item.client_phone || item.client_phone2 || '—'}
										</td>
										<td onClick={() => toggleRightbar(true, item, index)}
											className={`text-center text-white ${returnClassStatus(item.order_type)}`}>
											{item.cheque_number}
										</td>
										<td onClick={() => toggleRightbar(true, item, index)} className="text-center">
											{formatDate(item.cheque_opened_at, 'dd.MM.yy HH:mm')}
										</td>
										<td className={`text-end text-nowrap ${item.client_debtor_id ? 'bg-danger text-white' : ''}`}
											onClick={() => toggleRightbar(true, item, index)}>
											{formatMoney(item.total_amount_service)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<div className="operator-cheques-pagination">
							<FilterPagination
								pagination={pagination}
								paginate={paginateCheques}>
							</FilterPagination>
						</div>
					}
				</Modal.Body>
			</Modal>
			{/* MODAL CHEQUES */}

			{/* MODAL MODIFICATION */}
			<Modal show={modalModification.bool} animation={false} centered
				dialogClassName="modal-width-product-change" size="lg"
				onHide={() => setModalModification({ ...modalModification, 'bool': false, 'index': '', 'tabNumber': 'quantityTab' })}>
				<Modal.Body className="product-mod-modal-body">
					<div className="pick-modal-header">
						<div className="pick-modal-title">
							<i className="uil uil-edit"></i>
							<span className="product-mod-title-text">
								{modalModification.product?.name || t('quantity')}
							</span>
						</div>
						<button
							type="button"
							className="pick-modal-close"
							onClick={() => setModalModification({ ...modalModification, 'bool': false, 'index': '', 'tabNumber': 'quantityTab' })}>
							<i className="uil uil-times"></i>
						</button>
					</div>
					<div className="product-mod-layout">
						<div className="product-detail-tabs product-mod-tabs">
							<button type="button" className={`item ${modalModification.tabNumber === 'quantityTab' ? 'active' : ''}`}
								onClick={() => handleSelectModificationTab('quantityTab')}>
								<span className="product-detail-tab-icon"><i className="uil uil-calculator"></i></span>
								<span>{t('quantity')}</span>
							</button>
							<button type="button" className={`item ${modalModification.tabNumber === 'totalPriceTab' ? 'active' : ''}`}
								onClick={() => handleSelectModificationTab('totalPriceTab')}>
								<span className="product-detail-tab-icon"><i className="uil uil-bill"></i></span>
								<span>{t('amount')}</span>
							</button>
							<button type="button" className={`item ${modalModification.tabNumber === 'ingredientTab' ? 'active' : ''}`}
								onClick={() => handleSelectModificationTab('ingredientTab')}>
								<span className="product-detail-tab-icon"><i className="uil uil-clipboard-notes"></i></span>
								<span>{t('ingredients')}</span>
							</button>
							<button type="button" className={`item ${modalModification.tabNumber === 'commentTab' ? 'active' : ''}`}
								onClick={() => handleSelectModificationTab('commentTab')}>
								<span className="product-detail-tab-icon"><i className="uil uil-comment-alt-lines"></i></span>
								<span>{t('comments')}</span>
							</button>
							<button type="button" className={`item ${modalModification.tabNumber === 'salePriceTab' ? 'active' : ''}`}
								disabled={!modalModification.product?.is_custom_price}
								onClick={() => handleSelectModificationTab('salePriceTab')}>
								<span className="product-detail-tab-icon"><i className="uil uil-pricetag-alt"></i></span>
								<span>{t('change_price')}</span>
							</button>
						</div>
						<div className="product-mod-content">
							{modalModification.tabNumber === 'quantityTab' &&
								<div className="product-mod-keypad">
									<div className="product-mod-keypad-display product-mod-keypad-display-row">
										{String(modalModification?.product?.quantity ?? '').includes('.') ?
											<button type="button" className="numbers-item product-mod-x1"
												onClick={incrementDouble}>
												<div className="numbers-item-inner">x1</div>
											</button>
											:
											<div className="product-mod-x1-spacer"></div>
										}
										<input type="text" className="order-payment-input product-mod-value-input"
											autoFocus
											placeholder={modalModification.product.original_quantity}
											value={modalModification.product.quantity ? modalModification.product.quantity : ''}
											onChange={(e) => handleChangeModificationQuantity(e)}
										/>
									</div>
									<div className="product-mod-keypad-pad">
										<div className="numbers-wrapper">
											{numbers.map((item, index) => (
												<div className="numbers-item" key={index}
													onClick={() => clickProductQuantity(item.id)}>
													<div className="numbers-item-inner">
														{item.name}
													</div>
												</div>
											))}
										</div>
										<div className="numbers-wrapper product-mod-keypad-row">
											<button className="numbers-item"
												disabled={!modalModification?.product?.is_divisible}
												onClick={() => clickProductQuantity('.')}>
												<div className="numbers-item-inner">
													.
												</div>
											</button>
											<div className="numbers-item" onClick={() => clickProductQuantity('0')}>
												<div className="numbers-item-inner">
													0
												</div>
											</div>
											<div className="numbers-item" onClick={() => clickProductQuantity('C')}>
												<div className="numbers-item-inner">
													C
												</div>
											</div>
										</div>
									</div>
									<button
										type="button"
										disabled={!modalModification.product.quantity}
										className="btn btn-primary w-100 product-mod-action-btn"
										onClick={acceptModificationChange}>
										{t('confirm')}
									</button>
								</div>
							}
							{modalModification.tabNumber === 'totalPriceTab' &&
								<div className="product-mod-keypad">
									<div className="product-mod-keypad-display">
										<input type="text" className="order-payment-input product-mod-value-input"
											autoFocus
											placeholder={modalModification.product.original_total_price}
											value={modalModification.product.total_price ? modalModification.product.total_price : ''}
											onChange={(e) => handleChangeModificationTotalPrice(e)}
										/>
									</div>
									<div className="product-mod-keypad-pad">
										<div className="numbers-wrapper">
											{numbers.map((item, index) => (
												<div className="numbers-item" key={index}
													onClick={() => clickProductTotalPrice(item.id)}>
													<div className="numbers-item-inner">
														{item.name}
													</div>
												</div>
											))}
										</div>
										<div className="numbers-wrapper product-mod-keypad-row">
											<button className="numbers-item"
												disabled={!modalModification?.product?.is_divisible}
												onClick={() => clickProductTotalPrice('.')}>
												<div className="numbers-item-inner">
													.
												</div>
											</button>
											<div className="numbers-item" onClick={() => clickProductTotalPrice('0')}>
												<div className="numbers-item-inner">
													0
												</div>
											</div>
											<div className="numbers-item" onClick={() => clickProductTotalPrice('C')}>
												<div className="numbers-item-inner">
													C
												</div>
											</div>
										</div>
									</div>
									<button
										type="button"
										disabled={!modalModification.product.total_price}
										className="btn btn-primary w-100 product-mod-action-btn"
										onClick={acceptModificationChange}>
										{t('confirm')}
									</button>
								</div>
							}
							{modalModification.tabNumber === 'salePriceTab' &&
								<div className="product-mod-keypad">
									<div className="product-mod-keypad-display">
										<input type="text" className="order-payment-input product-mod-value-input"
											autoFocus
											placeholder={modalModification.product.original_sale_price}
											value={modalModification.product.sale_price ? modalModification.product.sale_price : ''}
											onChange={(e) => handleChangeModificationSalePrice(e)}
										/>
									</div>
									<div className="product-mod-keypad-pad">
										<div className="numbers-wrapper">
											{numbers.map((item, index) => (
												<div className="numbers-item" key={index}
													onClick={() => clickProductSalePrice(item.id)}>
													<div className="numbers-item-inner">
														{item.name}
													</div>
												</div>
											))}
										</div>
										<div className="numbers-wrapper product-mod-keypad-row">
											<button className="numbers-item"
												disabled={!modalModification?.product?.is_divisible}
												onClick={() => clickProductSalePrice('.')}>
												<div className="numbers-item-inner">
													.
												</div>
											</button>
											<div className="numbers-item" onClick={() => clickProductSalePrice('0')}>
												<div className="numbers-item-inner">
													0
												</div>
											</div>
											<div className="numbers-item" onClick={() => clickProductSalePrice('C')}>
												<div className="numbers-item-inner">
													C
												</div>
											</div>
										</div>
									</div>
									<button
										type="button"
										disabled={!modalModification.product.sale_price}
										className="btn btn-primary w-100 product-mod-action-btn"
										onClick={acceptModificationChange}>
										{t('confirm')}
									</button>
								</div>
							}
							{modalModification.tabNumber === 'ingredientTab' &&
								<div className="product-mod-ingredients">
									<div className="product-mod-ingredients-scroll">
										{modalModification?.product?.ingredients?.map((item, index) => (
											<Fragment key={index}>
												<div className={`product-ingredients-wrapper product-mod-ingredient ${item.del ? 'del' : ''}`}
													onClick={() => deleteIngredient(index)}>
													<div className="product-ingredients-left d-flex gap-2">
														<span className={`${!item.changeable ? 'text-danger' : ''}`}>
															{index + 1}.
														</span>
														<span>{item.name}</span>
													</div>
													<div className="product-ingredients-right gap-2">
														<div>{item.quantity}</div>
														<div className="product-ingredients-unit">{item.unit_name}</div>
													</div>
												</div>
											</Fragment>
										))}
									</div>
									<div className="product-mod-footer-actions">
										<button
											type="button"
											className="btn btn-outline-secondary product-mod-action-btn"
											onClick={() => setModalModification({ ...modalModification, 'bool': false, 'index': '', 'tabNumber': 'quantityTab' })}>
											{t('cancel')}
										</button>
										<button
											type="button"
											disabled={!modalModification.product.quantity}
											className="btn btn-primary product-mod-action-btn"
											onClick={acceptModificationChange}>
											{t('confirm')}
										</button>
									</div>
								</div>
							}
							{modalModification.tabNumber === 'commentTab' &&
								<div className="product-mod-comments">
									<div className="product-mod-comment-field">
										<label className="product-mod-field-label">{t('comment')}</label>
										<input type="text" className="form-control product-mod-comment-input"
											autoFocus
											autoComplete="off"
											name="product_comment"
											value={modalModification.product.comment ?? ''}
											onChange={(e) => setModalModification({
												...modalModification,
												'product': {
													...modalModification.product,
													'comment': e.target.value
												}
											})}
										/>
									</div>
									{comments.length === 0 &&
										<div className="no-comment-wrapper product-mod-comments-empty">
											<i className="uil uil-comment-alt-slash"></i>
											<span>{t('no_comment')}</span>
										</div>
									}
									{comments.length > 0 &&
										<div className="comment-wrapper product-mod-comments-grid">
											{comments?.map((item, index) => (
												<Fragment key={index}>
													<button type="button" className="comment-button product-mod-comment-btn"
														onClick={() => addCommentProduct(item)}>
														{item.children?.length > 0 &&
															<span className="comment-info">
																{item.children.length}
															</span>
														}
														<span className="comment-button-text">{item.name}</span>
													</button>
												</Fragment>
											))}
										</div>
									}
									<button type="button" className="btn btn-primary w-100 product-mod-action-btn"
										disabled={!(modalModification?.product.comment?.length)}
										onClick={addCommentProductAccept}>
										{t('apply')}
									</button>
								</div>
							}
						</div>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL MODIFICATION */}

			{/* MODAL MODIFIER */}
			{/* Клик мимо модалки = крестик: требования выполнены — просто закрываем,
			    нет — отменяем позицию. Целиться в крестик неудобно. */}
			<Modal show={modalModifiers} animation={false} centered
				dialogClassName="modal-width-modifiers"
				backdrop={true}
				keyboard={!modalModifiersForced}
				onHide={() => handleCloseModifiersModal()}>
				<Modal.Body className="pick-modal-body">
					<div className="pick-modal-header">
						<div className="pick-modal-title">
							<i className="uil uil-apps"></i>
							<span>{t('modifiers')}</span>
						</div>
						<button
							type="button"
							className="pick-modal-close"
							onClick={() => handleCloseModifiersModal()}>
							<i className="uil uil-times"></i>
						</button>
					</div>
					{modalModifiersForced &&
						<div className={`modifiers-modal-required-banner${unmetModifierGroups[0] ? '' : ' is-done'}`}>
							{unmetModifierGroups[0]
								? t('modifier_required_pick', {
									'product': modifiersParentProduct?.name || '',
									'group': unmetModifierGroups[0]?.groupName || '',
								})
								: t('modifier_required_done')
							}
						</div>
					}
					{isConstructorModal && !!modalSummary &&
						<div className="modifiers-modal-summary">
							<span>{modifiersParentProduct?.name} {formatMoney(modalSummary.base)}</span>
							<span>+ состав {formatMoney(modalSummary.extras)}</span>
							<b>= {formatMoney(modalSummary.total)}</b>
						</div>
					}
					<div className="modifiers-modal-scroll">
						{modifiers?.map((modifier, index) => {
							const groupStop = modifier.stop_list_quantity !== null
								&& modifier.stop_list_quantity !== ''
								&& Number(modifier.stop_list_quantity) === 0
							const groupName = modifier?.name != null && String(modifier.name).trim() !== ''
								? String(modifier.name).trim()
								: ''
							const parentProduct = resolveModifiersParentProduct()
							const parentProductId = parentProduct?.product_id
								?? data?.selectedProduct?.product_parent_id
								?? data?.selectedProduct?.product_id
							const groupNeed = getModifierGroupNeedQty(modifier)
							const groupMax = getModifierGroupMaxQty(modifier)
							const groupMin = normalizeModifierLimit(modifier.min)
							const groupQuantity = countModifierGroupQuantity(
								data?.products, parentProduct, modifier
							)
							const groupDraftQuantity = countModifierGroupQuantity(
								data?.products, parentProduct, modifier, true
							)
							const groupSelfQuantity = modifier.product_id
								? countModifierVariantQuantity(data?.products, parentProduct, modifier, modifier.product_id)
								: 0
							const groupSelfDraftQuantity = modifier.product_id
								? countModifierVariantQuantity(data?.products, parentProduct, modifier, modifier.product_id, true)
								: 0
							const groupUnmet = groupNeed > 0 && groupQuantity < groupNeed
							const groupAtMax = groupMax > 0 && groupQuantity >= groupMax
							// Сколько штук ещё бесплатно: лимит группы на все порции + «входит в цену» минус уже занятое
							const modalGroupId = getModifierGroupId(modifier)
							const groupFreeTotal = normalizeModifierLimit(modifier.free_quantity) * modalPortions
							const groupFreeLeft = Math.max(0, groupFreeTotal - (modalSummary?.freeUsed?.[modalGroupId] || 0))
							const groupIncludedTotal = (modifier.products || []).reduce((sum, item) => (
								item.base_price_included ? sum + normalizeModifierLimit(item.default_quantity) * modalPortions : sum
							), 0)
							const groupIncludedLeft = Math.max(0, groupIncludedTotal - (modalSummary?.includedUsed?.[modalGroupId] || 0))
							const groupFreeAvailable = groupFreeLeft + groupIncludedLeft

							return (
								<div
									className={`modifiers-modal-group${groupUnmet ? ' is-required-unmet' : ''}${groupNeed > 0 && !groupUnmet ? ' is-required-met' : ''}`}
									key={modifier.product_modifier_id || modifier.id || index}>
									{!!groupName &&
										<div className="modifiers-modal-group-head">
											<div className="modifiers-modal-group-title">
												{groupName}
												{isModifierGroupRequired(modifier) ? ' *' : ''}
											</div>
											{groupDraftQuantity > 0 &&
												<button type="button" className="modifiers-modal-group-clear"
													onClick={() => removeModifier(modifier, null, parentProductId, true)}>
													{t('modifier_group_clear')}
												</button>
											}
										</div>
									}
									<div className="modifiers-modal-group-limits">
										{groupMin > 0 &&
											<span>{t('modifier_min')}: {groupMin}</span>
										}
										{groupMax > 0 &&
											<span>{t('modifier_max')}: {groupMax}</span>
										}
										<span className={`modifiers-modal-group-chosen${groupUnmet ? ' is-unmet' : ''}`}>
											{t('modifier_chosen')}: {groupQuantity}
										</span>
										{groupFreeTotal + groupIncludedTotal > 0 &&
											<span className={`modifiers-modal-group-free${groupFreeAvailable === 0 ? ' is-empty' : ''}`}>
												Бесплатно: {groupFreeAvailable}
											</span>
										}
									</div>
									<div className="pick-modal-grid is-list">
										{!!(modifier.product_id && groupName) &&
											<div className="pick-modal-card-slot">
												<button
													type="button"
													className={`pick-modal-card${groupStop ? ' is-stop' : ''}${groupSelfQuantity > 0 ? ' is-picked' : ''}`}
													disabled={groupStop || (groupAtMax && !isModifierSlotGroup(modifier))}
													title={groupAtMax && !isModifierSlotGroup(modifier) ? t('modifier_group_at_max') : undefined}
													onClick={() => {
														if (groupStop || (groupAtMax && !isModifierSlotGroup(modifier))) return
														chooseModifier(modifier, modifier, parentProductId)
													}}>
													{groupSelfQuantity > 0 &&
														<div className="pick-modal-card-qty">{groupSelfQuantity}</div>
													}
													<div className="pick-modal-card-name">{modifier.name}</div>
													{!!(
														reduxAccount?.pos_settings?.bool_show_sale_price &&
														Number(modifier.sale_price) > 0
													) &&
														<div className="pick-modal-card-price">
															{formatMoney(modifier.sale_price)}
														</div>
													}
													{(modifier.stop_list_quantity >= 0 && modifier.stop_list_quantity !== null) &&
														<div className={`pick-modal-card-stock${groupStop ? ' is-stop' : ''}`}>
															{groupStop ? 'STOP' : formatMoney(modifier.stop_list_quantity)}
														</div>
													}
												</button>
												{groupSelfDraftQuantity > 0 &&
													<button type="button" className="pick-modal-card-minus"
														title={t('modifier_remove_one')}
														onClick={() => removeModifier(modifier, modifier, parentProductId)}>
														<i className="uil uil-minus"></i>
													</button>
												}
											</div>
										}
										{modifier?.products?.map((item, index2) => {
											const isStop = item.stop_list_quantity !== null
												&& item.stop_list_quantity !== ''
												&& Number(item.stop_list_quantity) === 0
											const itemQuantity = countModifierVariantQuantity(
												data?.products, parentProduct, modifier, item.product_id
											)
											const itemDraftQuantity = countModifierVariantQuantity(
												data?.products, parentProduct, modifier, item.product_id, true
											)
											const isPicked = itemQuantity > 0
											// В слоте карточка остаётся кликабельной: она заменяет набранное («фри ×2» → «крылышки ×2»)
											const cardDisabled = isStop || (groupAtMax && !isModifierSlotGroup(modifier) && !isPicked)
											const disabledHint = isStop
												? t('stop_list')
												: (cardDisabled ? t('modifier_group_at_max') : undefined)

											return (
												<div className="pick-modal-card-slot" key={item.product_id || index2}>
													<button
														type="button"
														className={`pick-modal-card${isStop ? ' is-stop' : ''}${isPicked ? ' is-picked' : ''}`}
														disabled={cardDisabled}
														title={disabledHint}
														onClick={() => {
															if (cardDisabled) return
															chooseModifier(modifier, item, parentProductId)
														}}>
														{itemQuantity > 0 &&
															<div className="pick-modal-card-qty">{itemQuantity}</div>
														}
														<div className="pick-modal-card-name">{item.name}</div>
														{Number(item.sale_price) > 0 && groupFreeAvailable > 0 ?
															<div className="pick-modal-card-price is-free">бесплатно</div>
														: !!(
															reduxAccount?.pos_settings?.bool_show_sale_price &&
															Number(item.sale_price) > 0
														) &&
															<div className="pick-modal-card-price">
																{formatMoney(item.sale_price)}
															</div>
														}
														{(item.stop_list_quantity >= 0 && item.stop_list_quantity !== null) &&
															<div className={`pick-modal-card-stock${isStop ? ' is-stop' : ''}`}>
																{isStop ? 'STOP' : formatMoney(item.stop_list_quantity)}
															</div>
														}
													</button>
													{itemDraftQuantity > 0 &&
														<button type="button" className="pick-modal-card-minus"
															title={t('modifier_remove_one')}
															onClick={() => removeModifier(modifier, item, parentProductId)}>
															<i className="uil uil-minus"></i>
														</button>
													}
												</div>
											)
										})}
									</div>
								</div>
							)
						})}
					</div>
					<button
						type="button"
						className="btn btn-primary w-100 mt-2"
						disabled={!canCloseRequiredModifiersModal()}
						onClick={() => tryCloseModifiersModal()}>
						{t('apply')}
					</button>
				</Modal.Body>
			</Modal>
			{/* MODAL MODIFIER */}

			{/* MODAL SIZES */}
			<Modal show={modalSizes.bool} animation={false} centered
				dialogClassName="modal-width-sizes"
				onHide={() => setModalSizes({
					'bool': false,
					'replace': false,
					'index': '',
				})}>
				<Modal.Body className="pick-modal-body">
					<div className="pick-modal-header">
						<div className="pick-modal-title">
							<i className="uil uil-layer-group"></i>
							<span>{t('sizes')}</span>
						</div>
						<button
							type="button"
							className="pick-modal-close"
							onClick={() => setModalSizes({
								'bool': false,
								'replace': false,
								'index': '',
							})}>
							<i className="uil uil-times"></i>
						</button>
					</div>
					<div className="pick-modal-grid is-list">
						{sizes?.map((item, index) => {
							const isActive = modalSizes.replace
								&& Number(data?.selectedProduct?.product_id) === Number(item.product_id)
							const isStop = item.stop_list_quantity !== null
								&& item.stop_list_quantity !== ''
								&& Number(item.stop_list_quantity) === 0

							return (
								<button
									type="button"
									key={item.product_id || index}
									className={`pick-modal-card${isActive ? ' is-active' : ''}${isStop ? ' is-stop' : ''}`}
									disabled={isStop}
									onClick={() => {
										if (isStop) return
										if (modalSizes.replace) {
											replaceSize(item)
										} else {
											addToList(item)
											setModalSizes({
												'bool': false,
												'replace': false,
												'index': '',
											})
										}
									}}>
									<div className="pick-modal-card-name">{item.name}</div>
									{!!(
										reduxAccount?.pos_settings?.bool_show_sale_price &&
										Number(item.sale_price) > 0
									) &&
										<div className="pick-modal-card-price">
											{formatMoney(item.sale_price)}
										</div>
									}
									{(item.stop_list_quantity >= 0 && item.stop_list_quantity !== null) &&
										<div className={`pick-modal-card-stock${isStop ? ' is-stop' : ''}`}>
											{isStop ? 'STOP' : formatMoney(item.stop_list_quantity)}
										</div>
									}
								</button>
							)
						})}
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL SIZES */}

			{/* MODAL MAP */}
			<Modal show={modalMap} animation={false}
				dialogClassName="custom-delivery-modal"
				onHide={() => toggleModalMap()}>
				<Modal.Body>
					<div className="row">
						<div className="col-md-12 position-relative" style={{ height: '80vh' }} >
							{/* Поиск адреса поверх карты. SearchControl из JS API ищет через сервис, который
								не принимает ключи нового кабинета Яндекса, поэтому — своё поле на HTTP-геокодере */}
							<AddressSuggestField
								className="map-address-search"
								mapToken={mapToken}
								boundedBy={mapSearchBounds}
								placeholder={t('search_address')}
								onPick={(picked) => pickMapAddress(picked, true)} />
							<YMaps
								style={{ height: '80vh' }}
								query={{
									load: "package.full",
									apikey: mapToken
								}}>
								<Map
									instanceRef={(mapInstance) => { mapRef.current = mapInstance; }}
									style={{ height: '80vh' }}
									defaultOptions={{
										copyrightUaVisible: false,
										yandexMapDisablePoiInteractivity: true,
									}}
									defaultState={defaultState}
									onClick={(event) => {
										const coords = event.get("coords");
										pickMapPoint(Number(coords[0]), Number(coords[1]));
									}}
								>
									{newAddress.latitude &&
										<Placemark geometry={[newAddress.latitude, newAddress.longitude]} />
									}
									{reduxDelivery?.poses.map((item, index) => (
										<Placemark
											key={index}
											options={posPlacemarkOptions}
											properties={posPlacemarkProperties({
												name: item.name,
												phone: item.phone,
												t: t,
											})}
											geometry={[Number(item.latitude), Number(item.longitude)]} />
									))}
								</Map>
							</YMaps>
						</div>
					</div>
					<div className="d-flex gap-2 w-100 mt-3">
						<button type="button" className="btn btn-outline-warning w-100" style={{ 'flex': 1 }}
							onClick={() => toggleModalMap()}>
							{t('cancel')}
						</button>
						<button type="submit" className="btn btn-primary w-100" style={{ 'flex': 3 }}
							onClick={() => createClientAddress()}>
							{newAddress?.distance ?
								<span className="ms-3">{newAddress?.pos_name} {newAddress?.distance}km </span>
								:
								<span>{t('save')}</span>
							}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL MAP */}

			{/* MODAL CLIENT */}
			<Modal show={modalClient} animation={false} centered
				dialogClassName="update-modal-width" onHide={() => toggleModalClient()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('edit')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={(e) => updateClient(e)} autoComplete="off">
						<div className="form-group">
							<label>{t('name')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" name="first_name"
								value={selectedItem.first_name ?? ''}
								onChange={(e) => handleClientChange(e)} />
						</div>
						<div className="form-group">
							<label>{t('additional_phone')}</label>
							<input type="number" className="form-control" name="phone2"
								value={selectedItem.phone2 ?? ''}
								onChange={(e) => handleClientChange(e)} />
						</div>
						<div className="form-group">
							<label>
								{t('birthday')}
								{isBirthdayLocked && <HintIcon text={t('birthday_locked_hint')} />}
							</label>
							<input type="date" className="form-control" name="birthday"
								disabled={isBirthdayLocked}
								min="1900-01-01"
								value={selectedItem.birthday ?? ''}
								onChange={(e) => handleClientChange(e)} />
						</div>
						<div className="d-flex justify-content-between mb-2">
							<div className="d-flex gap-2">
								<div className="form-check p-0">
									<div className="radio-round"
										onClick={() => setSelectedItem({ ...selectedItem, 'gender': 1 })}
										style={{ 'backgroundColor': selectedItem.gender === 1 ? 'black' : '' }}>
									</div>
									<span onClick={() => setSelectedItem({ ...selectedItem, 'gender': 1 })}>
										{t('man')}
									</span>
								</div>

								<div className="form-check p-0">
									<div className="radio-round"
										onClick={() => setSelectedItem({ ...selectedItem, 'gender': 2 })}
										style={{ 'backgroundColor': selectedItem.gender === 2 ? 'black' : '' }}>
									</div>
									<span onClick={() => setSelectedItem({ ...selectedItem, 'gender': 2 })}>
										{t('woman')}
									</span>
								</div>
							</div>
						</div>
						<div className="form-group">
							<label>{t('note')}</label>
							<input type="text" className="form-control" name="note"
								value={selectedItem.note ?? ''}
								onChange={(e) => handleClientChange(e)} />
						</div>
						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModalMap()}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL CLIENT */}

			{/* MODAL ADDRESS DELETE */}
			<Modal show={modalAddressDelete.bool} animation={false}
				centered dialogClassName="update-modal-width"
				onHide={() => toggleModalAddressDelete(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('delete')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{t('are_you_sure_you_want_delete')}
				</Modal.Body>
				<Modal.Footer >
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalAddressDelete(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-danger w-100"
							onClick={() => deleteClientAddress()}>
							{t('delete')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL ADDRESS DELETE */}

			{/* MODAL DELETION REASON */}
			<Modal show={modalDeletionReason.bool} animation={false}
				centered
				dialogClassName="delivery-comment-modal"
				onHide={() => setModalDeletionReason({ 'bool': false, 'action': null, 'reason_id': null, 'comment': '' })}>
				<Modal.Header closeButton>
					<Modal.Title>{t('deletion_reason')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="product-mod-comments">
						<div className="comment-wrapper product-mod-comments-grid">
							{(deletionSettings?.reasons || []).map((item) => (
								<button type="button" key={item.id}
									className={`comment-button product-mod-comment-btn${modalDeletionReason.reason_id === item.id ? ' active' : ''}`}
									style={modalDeletionReason.reason_id === item.id ? { borderColor: 'var(--bs-primary)', color: 'var(--bs-primary)' } : {}}
									onClick={() => setModalDeletionReason({ ...modalDeletionReason, 'reason_id': item.id })}>
									<span className="comment-button-text">{item.name}</span>
								</button>
							))}
						</div>
						<div className="product-mod-comment-field">
							<label className="product-mod-field-label">{t('comment')}</label>
							<input type="text" className="form-control product-mod-comment-input"
								autoComplete="off"
								maxLength={255}
								value={modalDeletionReason.comment}
								onChange={(e) => setModalDeletionReason({ ...modalDeletionReason, 'comment': e.target.value })}
							/>
						</div>
						<button type="button" className="btn btn-primary w-100 product-mod-action-btn"
							disabled={!modalDeletionReason.reason_id}
							onClick={() => applyDeletionReason()}>
							{t('apply')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL DELETION REASON */}

			{/* MODAL COMMENT */}
			<Modal show={modalComment.bool} animation={false}
				centered
				dialogClassName="delivery-comment-modal"
				onHide={() => toggleModalComment(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						{modalComment.type === 'note' ? t('cheque_comment') : t('kitchen_comment')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="product-mod-comments">
						<div className="product-mod-comment-field">
							<label className="product-mod-field-label">
								{modalComment.type === 'note' ? t('cheque_comment') : t('kitchen_comment')}
							</label>
							<input type="text" className="form-control product-mod-comment-input"
								autoFocus
								autoComplete="off"
								name={modalComment.type === 'note' ? 'note' : 'comment'}
								value={(modalComment.type === 'note' ? data.note : data.comment) ?? ''}
								onChange={(e) => setData({
									...data,
									[modalComment.type === 'note' ? 'note' : 'comment']: e.target.value
								})}
							/>
						</div>
						{comments.length === 0 &&
							<div className="no-comment-wrapper product-mod-comments-empty">
								<i className="uil uil-comment-alt-slash"></i>
								<span>{t('no_comment')}</span>
							</div>
						}
						{comments.length > 0 &&
							<div className="comment-wrapper product-mod-comments-grid">
								{comments?.map((item, index) => (
									<Fragment key={index}>
										<button type="button" className="comment-button product-mod-comment-btn"
											onClick={() => addComment(item)}>
											{item.children?.length > 0 &&
												<span className="comment-info">
													{item.children.length}
												</span>
											}
											<span className="comment-button-text">{item.name}</span>
										</button>
									</Fragment>
								))}
							</div>
						}
						<button type="button" className="btn btn-primary w-100 product-mod-action-btn"
							disabled={!((modalComment.type === 'note' ? data.note : data.comment)?.length)}
							onClick={() => toggleModalComment(false)}>
							{t('apply')}
						</button>
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL COMMENT */}

			{/* MODAL EXTERNAL COURIER */}
			<Modal show={modalExternalCourier} animation={false}
				size="sm"
				style={{ 'marginTop': '40px', 'overflow': 'auto' }}
				onHide={() => toggleModalExternalCourier(false)}>
				<Modal.Body>
					{selectedExternalCouriers?.length === 0 &&
						<div className="no-comment-wrapper" style={{ 'height': '300px' }}>
							<h4>{t('list_empty')}</h4>
						</div>
					}
					{selectedExternalCouriers?.length > 0 &&
						<div className="comment-wrapper no-scroll" style={{ 'height': '300px' }}>
							{selectedExternalCouriers?.map((item, index) => (
								<Fragment key={index}>
									<div className="order-actions-item"
										style={item?.isUnavailable ? { 'opacity': 0.6, 'cursor': 'not-allowed' } : {}}
										onClick={() => handleClickExternalCourier(item)}>
										<div className="d-flex flex-column align-items-center gap-2">
											<div className="d-flex gap-2">
												<span>{item.name}</span>
												<span className="text-nowrap fw-bold text-primary">
													{item?.delivery_price !== null && item?.delivery_price !== undefined
														? <b>{formatMoney(item.delivery_price)}</b>
														: '- X'
													}
												</span>
											</div>
											{!item?.success && item?.message &&
												<div className="text-danger fz-12">
													{item.message}
												</div>
											}
										</div>
									</div>
								</Fragment>
							))}
						</div>
					}
				</Modal.Body>
			</Modal>
			{/* MODAL EXTERNAL COURIER */}

			{/* MODAL EXTERNAL COURIER */}
			<Modal show={modalExternalMenu} animation={false}
				size="sm"
				style={{ 'marginTop': '40px', 'overflow': 'auto' }}
				onHide={() => toggleModalExternalMenu(false)}>
				<Modal.Body>
					{reduxDelivery.externalMenu?.length === 0 &&
						<div className="no-comment-wrapper" style={{ 'height': '300px' }}>
							<h4>{t('list_empty')}</h4>
						</div>
					}
					{reduxDelivery.externalMenu?.length > 0 &&
						<div className="comment-wrapper no-scroll" style={{ 'height': '300px' }}>
							{reduxDelivery.externalMenu?.map((item, index) => (
								<Fragment key={index}>
									<div className="order-actions-item"
										onClick={() => handleClickExternalMenu(item)}>
										{item.name}
									</div>
								</Fragment>
							))}
						</div>
					}
				</Modal.Body>
			</Modal>
			{/* MODAL EXTERNAL COURIER */}

			<Rightbar
				showRightBar={showRightBar}
				setShowRightBar={setShowRightBar}
				selectedItem={selectedItem}>
			</Rightbar>
		</>
	)
}

export default OrderCreate
